// English translations for FLEETORA
const en = {
  // Locale identifier for date formatting
  locale: 'en-US',
  
  // Platform
  platform: {
    name: "FLEETORA",
    subtitle: "Operational Oversight Platform",
    byLine: "by Aeroway",
  },

  organizationScope: {
    allOrganizations: "All organizations",
    selectPlaceholder: "Select organization",
    selectTitle: "Select an organization",
    selectDescription: "Select the organization you want to work on behalf of before opening this page.",
    selectAction: "Go to organization selection",
    viewOrganization: "Select / view as",
    viewingOrganization: "Active organization",
    loadError: "Failed to load the organization list.",
    retryAction: "Try again",
    formDirtyConfirm: "Closing this form will discard your unsaved changes. Switch organization anyway?",
  },
  
  // Common (merged - single definition)
  common: {
    organization: "Organization",
    status: "Status",
    date: "Date",
    actions: "Actions",
    loading: "Loading...",
    uploading: "Uploading...",
    processing: "Processing...",
    upload: "Upload",
    cancel: "Cancel",
    noData: "No data available",
    error: "An error occurred",
    success: "Success",
    confirm: "Confirm",
    yes: "Yes",
    no: "No",
    dropFilesHint: "Drop files here or click Upload",
    info: "Information",
    all: "All",
    none: "None",
    notes: "Notes",
    language: "Language",
    english: "English",
    czech: "Czech",
    optional: "optional",
    defined: "Defined",
    unlimited: "Unlimited",
    upgrade: "Upgrade",
    current: "Current",
    included: "Included",
    locked: "Locked",
  },
  
  // Subscription & Tiers
  subscription: {
    title: "Subscription",
    currentPlan: "Current Plan",
    yourPlan: "Your Plan",
    features: "Features",
    limits: "Limits",
    usage: "Usage",
    upgradeTitle: "Upgrade Your Plan",
    upgradeDescription: "Unlock more features and remove limits",
    contactUs: "Contact Us to Upgrade",
    contactEmail: "sales@fleetora.com",
    tiers: {
      free: "Free",
      professional: "Professional",
      enterprise: "Enterprise"
    },
    tierDescriptions: {
      free: "Basic drone operations tracking",
      professional: "Full compliance toolkit for professional operators",
      enterprise: "Complete platform with API access and SSO"
    },
    featureLabels: {
      checkmate: "CheckMate SORA 2.5 Engine",
      sora: "SORA Pre-Assessment",
      ai_document_generation: "AI Document Generation",
      document_review: "AI Document Review",
      ucl_submissions: "UCL Submission Tracker",
      document_storage: "Document Storage",
      scenarios: "Scenario Management",
      insurance_tracking: "Insurance Tracking",
      authorizations: "Authorization Management",
      bulletins: "Regulatory Bulletins",
      reports: "Reports & Analytics",
      api_access: "API Access",
      sso: "Single Sign-On (SSO)",
      audit_logs: "Audit Logs",
      priority_support: "Priority Support"
    },
    resourceLabels: {
      pilots: "Pilots",
      aircraft: "Aircraft",
      batteries: "Batteries",
      missions: "Missions",
      documents: "Documents"
    },
    locked: {
      title: "Premium Feature",
      requires: "This feature requires",
      upgrade: "Upgrade",
      viewPlans: "View Plans"
    }
  },

  // Navigation modules
  nav: {
    dashboard: "Dashboard",
    flightOps: "FlightOps",
    crew: "Crew",
    fleet: "Equipment",
    equipment: "Equipment",
    safety: "Incidents",
    compliance: "Compliance",
    intelligence: "Intelligence",
    reports: "Reports",
    users: "Users",
    organizations: "Organizations",
    alertHistory: "Alert History",
    auditSimulation: "Audit Simulation",
    knowledgeBase: "Knowledge Base Management",
    insurance: "Insurance",
    authorizations: "Authorizations",
    submissions: "UCL Submissions",
    news: "News & Regulations",
    bulletins: "Bulletins",
    correspondence: "Correspondence",
    fleetMap: "Fleet Map",
  },
  
  // User roles
  roles: {
    platform_admin: "Platform Admin",
    organization_admin: "Org Admin",
    compliance_manager: "Compliance Manager",
    pilot: "Pilot",
    operator: "Operator",
    viewer: "Viewer",
  },
  
  // Sub-navigation items
  subNav: {
    // FlightOps
    missions: "Missions",
    missionDetail: "Mission Detail",
    missionChecklist: "Mission Checklist",
    dailyOps: "Daily Ops",
    missionCalendar: "Mission Calendar",
    missionHistory: "Mission History",
    
    // Crew
    pilots: "Pilots",
    certifications: "Certifications",
    
    // Fleet
    aircraft: "Aircraft",
    batteries: "Batteries",
    equipment: "Equipment",
    
    // Safety
    incidents: "Incidents",
    investigation: "Investigation",
    
    // Compliance
    scenarios: "Scenarios",
    documents: "Documents",
    complianceChecks: "Compliance Checks",
    
    // Reports
    pdfReports: "PDF Reports",
    csvExport: "CSV Export",
    missionBrief: "Mission Brief",
  },
  
  // Page Descriptions
  pageDescriptions: {
    missions: "Flight Operations",
    pilots: "Manage pilot certifications and authorizations",
    aircraft: "Manage your drone fleet and maintenance",
    batteries: "Track battery lifecycle and safety",
    scenarios: "Manage approved operational scenarios (ConOps, SORA)",
    documents: "Track regulatory documents and compliance records",
    incidents: "Report and track safety incidents",
    missionDetail: "Mission Overview",
  },
  
  // Common actions
  actions: {
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    create: "Create",
    add: "Add",
    update: "Update",
    search: "Search",
    filter: "Filter",
    export: "Export",
    import: "Import",
    download: "Download",
    upload: "Upload",
    generate: "Generate",
    view: "View",
    back: "Back",
    submit: "Submit",
    confirm: "Confirm",
    close: "Close",
    resolve: "Resolve",
    report: "Report",
    markAsResolved: "Mark as Resolved",
    refresh: "Refresh",
  },
  
  // Auth
  auth: {
    login: "Login",
    logout: "Logout",
    email: "Email",
    password: "Password",
    showPassword: "Show password",
    hidePassword: "Hide password",
    signIn: "Sign In",
    welcomeBack: "Welcome back",
    enterCredentials: "Enter your credentials to access the platform",
    // Login page hero section
    heroDescription: "Ensure regulatory compliance, maintain audit readiness, and monitor your drone operations with confidence.",
    featureEASA: "EASA Open & Specific Category Compliance",
    featureMonitoring: "Real-time Compliance Monitoring",
    featureCertification: "Automated Certification Tracking",
    // Loading screen
    loadingText: "Initializing FLEETORA",
  },

  // Application version in the user menu
  appVersion: {
    version: "Version {version}",
    built: "build {date}",
  },
  
  // Dashboard - Daily Overview (Operational Dispatch)
  dashboard: {
    live: "LIVE",
  },
  
  // Analytics Dashboard
  analytics: {
    title: "Analytics Dashboard",
    subtitle: "Operational insights and U-Space readiness",
    operationalOverview: "Operational Overview",
    missionAnalysis: "Mission Analysis",
    safetyAndUSpace: "Safety, Compliance & U-Space",
    documentExpiry: "Document Monitoring",
    totalMissions: "Missions This Period",
    avgTimeToReady: "Avg. Time to Ready",
    fromCreation: "from creation",
    activePilots: "Active Pilots",
    thisPeriod: "this period",
    incidentsThisMonth: "Incidents",
    missionStatusBreakdown: "Mission Status Breakdown",
    sailDistribution: "SAIL Level Distribution",
    safetyCompliance: "Safety & Compliance",
    complianceRate: "Compliance Rate",
    readyBeforeMission: "Missions ready before date",
    soraConfidence: "SORA Confidence Rate",
    topMissingReq: "Top Missing Requirements",
    uSpaceReadiness: "U-Space Readiness",
    fleetStatus: "Fleet Status",
    drones: "drones",
    topFlightHours: "Top Flight Hours",
    approachingService: "Approaching Service Interval",
    allDronesOk: "All drones within service limits",
    documentAlerts: "Document Expiry Alerts",
    expiring30: "Expiring in 30 days",
    expiring90: "Expiring in 31-90 days",
    noExpiringDocs: "No documents expiring",
    week: "This Week",
    month: "This Month",
    quarter: "This Quarter",
    year: "This Year",
    allOrgs: "All Organizations",
    status: {
      draft: "Draft",
      in_progress: "In Progress",
      ready: "Ready",
      completed: "Completed",
      cancelled: "Cancelled"
    }
  },
  
  // FlightOps / Missions
  flightOps: {
    title: "Flight Operations",
    missions: "Missions",
    logMission: "Log Mission",
    missionId: "Mission ID",
    missionDate: "Mission Date",
    location: "Site name",
    locationHelper: "Brief description of the site (e.g., 'V401 powerline inspection')",
    missionLocationHelper: "Click on map or search address",
    pilot: "Pilot",
    aircraft: "Aircraft",
    scenario: "Scenario",
    readiness: "Readiness",
    compliance: "Compliance",
    missionDetail: "Mission Detail",
    missionOverview: "Mission Overview",
    operationsPlanning: "Operations Planning",
    address: "Address",
    generateBrief: "Generate Mission Brief",
    briefLanguage: "Brief Language",
    editMission: "Edit Mission",
    logNewMission: "Log New Mission",
    missionNotes: "Mission notes...",
    confirmMitigation: "I confirm all risk mitigations were followed for this mission",
    confirmMitigationRequired: "Please confirm that all risk mitigation measures were followed",
    selectOrganization: "Select organization",
    selectPilot: "Select pilot",
    selectAircraft: "Select aircraft",
    selectScenario: "Select scenario (recommended)",
    operationalScenario: "Operational Scenario",
    notes: "Notes",
    missionInformation: "Mission Information",
    noScenarioAssigned: "No scenario assigned",
    missionNotFound: "Mission not found",
    backToMissions: "Back to Missions",
    noChecklistAvailable: "No checklist available",
    complete: "complete",
    attachments: "Attachments",
    noAttachments: "No attachments yet",
    uploadHint: "Upload mission evidence, reports, or images",
    imagePreview: "Image Preview",
    imageLoadError: "Failed to load image",
    // Location / Map
    coordinates: "Coordinates",
    clickToSet: "Click map to set location",
    latitude: "Latitude",
    longitude: "Longitude",
    missionLocation: "Mission Location",
    noCoordinates: "No coordinates set",
    editToAddLocation: "Edit mission to add location",
    locationRequired: "Set location to enable airspace verification",
    setLocation: "Set Location",
    searchLocation: "Search address or place...",
    openInDroneMap: "Open in DroneMap",
    droneMapDisclaimer: "For official airspace and regulatory restrictions, please verify this location in DroneMap.",
    copyAddress: "Copy Address",
    copyCoordinates: "Copy Coordinates",
    addressCopied: "Address copied to clipboard",
    coordinatesCopied: "Coordinates copied to clipboard",
    clipboardCopyError: "Failed to copy to clipboard",
    verifyInDroneMap: "Verify in DroneMap",
    droneMapHelper: "Copy the location and paste it into DroneMap search.",
    // NEW: Max altitude
    maxAltitude: "Max. flight altitude (m AGL)",
    maxAltitudeHelper: "Highest height above ground level anywhere in the flight area",
    maxAltitudeShort: "Max altitude",
    // Recommended altitude from the ŘLP height grid under the flight area (lib/heightLimit.js)
    altitudeGrid: {
      autoSingle: "Set from the ŘLP height grid under the flight area: {value} m.",
      auto: "Set from the ŘLP height grid under the flight area: {value} m (lowest of {cells} cells).",
      matches: "Matches the ŘLP height grid under the flight area ({value} m).",
      manual: "ŘLP height grid under the flight area: {value} m — entered manually.",
      stored: "ŘLP height grid under the flight area: {value} m (saved value kept).",
      differs: "ŘLP height grid under the flight area: {value} m.",
      above: "The altitude entered is above the ŘLP grid ({value} m).",
      apply: "Use the recommended {value} m",
      partial: "Part of the area lies outside the grid; the value does not apply there.",
      zero: "The flight area reaches into an ŘLP height grid cell of 0 m — no altitude can be recommended from the grid; enter it manually.",
      outside: "The flight area does not reach the ŘLP height grid — set the altitude for your operation.",
      error: "The altitude from the ŘLP grid could not be determined.",
      loading: "Looking up the altitude in the ŘLP grid…",
      disclaimer: "A recommendation from the ŘLP data, not a flight permission.",
      overwritten: "Flight altitude adjusted to the ŘLP height grid: {value} m.",
    },
    saveMissionError: "Failed to save mission. Please check all fields and try again.",
  },
  
  // Mission states
  missionStatus: {
    draft: "Draft",
    inProgress: "In Progress",
    ready: "Ready",
    notReady: "Not Ready",
    compliant: "Compliant",
    nonCompliant: "Non-Compliant",
  },
  
  // Compliance Detail
  complianceDetail: {
    title: "Compliance Status",
    checklistComplete: "Checklist complete",
    mitigationConfirmed: "Mitigation confirmed",
    documentsValid: "Documents valid",
    pilotAuthorized: "Pilot authorization",
    sailAcceptable: "SAIL level acceptable",
    soraConfidence: "SORA confidence",
    soraRequirements: "SORA requirements",
    advisory: "advisory",
    blocking: "blocking",
    items: "items",
    missing: "missing",
    expired: "expired",
    highSailWarning: "SAIL V/VI requires special authorization",
    pilotCertExpired: "Pilot certificate expired",
    aircraftInsuranceExpired: "Aircraft insurance expired",
    noBlockingIssues: "No blocking issues",
  },
  
  // Checklist
  checklist: {
    title: "Pre-Mission Checklist",
    saveChecklist: "Save Checklist",
    equipment: "Equipment",
    pilotSection: "Pilot",
    documentation: "Documentation",
    aircraftReady: "Aircraft technically ready",
    batteryChecked: "Battery status checked",
    equipmentAssigned: "Equipment assigned",
    pilotAssigned: "Pilot assigned",
    licenseVerified: "License verified",
    internalAuthConfirmed: "Internal authorization confirmed",
    scenarioAssigned: "Scenario assigned",
    docsAvailable: "Required documentation available",
    conopsReviewed: "ConOps / procedures reviewed",
    // Section names (from backend checklist)
    preFlightPlanning: "Pre-Flight Planning",
    equipmentCheck: "Equipment Check",
    pilotReadiness: "Pilot Readiness",
    operationalArea: "Operational Area",
    // Checklist items
    missionDateSet: "Mission date set",
    locationDefined: "Location defined",
    aircraftAssigned: "Aircraft assigned",
    pilotAssignedItem: "Pilot assigned",
    scenarioSelected: "Scenario selected",
    aircraftOperational: "Aircraft operational",
    batteryCharged: "Battery charged",
    equipmentInspected: "Equipment inspected",
    pilotCertValid: "Pilot certification valid",
    pilotBriefed: "Pilot briefed",
    weatherChecked: "Weather checked",
    airspaceChecked: "Airspace checked",
    notamReviewed: "NOTAM reviewed",
    areaSecured: "Area secured",
  },
  
  // Checklist states
  checklistStatus: {
    notStarted: "Not started",
    inProgress: "In progress",
    completed: "Completed",
  },
  
  // Crew / Pilots
  crew: {
    title: "Crew Management",
    pilots: "Pilots",
    addPilot: "Add Pilot",
    editPilot: "Edit Pilot",
    pilotName: "Pilot Name",
    pilotEmail: "Email",
    authorizations: "Authorizations & Certifications",
    authA1A3: "A1/A3 Certificate (Open Category)",
    authA2: "A2 Certificate (Open Category - Close to People)",
    authSpecific: "STS authorization",
    authInternal: "Internal Company Authorization / Training",
    issueDate: "Issue Date",
    expirationDate: "Expiration Date",
    trainingDate: "Training/Issue Date",
    expirationOptional: "Expiration Date (Optional)",
    valid: "Valid",
    expiring: "Expiring",
    invalid: "Invalid",
    expired: "Expired",
    pilotsExpiredAlert: "pilot(s) with expired certifications",
    pilotsExpiringAlert: "pilot(s) with expiring certifications",
    searchPilots: "Search pilots...",
    noPilotsFound: "No pilots found",
    documents: "Documents",
    noDocuments: "No documents uploaded",
    uploadHint: "Upload license scans, training certificates, or other documents",
    license: "License",
    trainingCertificate: "Training Certificate",
    medical: "Medical Certificate",
  },
  
  // Fleet
  fleet: {
    title: "Fleet Management",
    aircraft: "Aircraft",
    aircraftFleet: "Aircraft Fleet",
    batteries: "Batteries",
    addAircraft: "Add Aircraft",
    editAircraft: "Edit Aircraft",
    addBattery: "Add Battery",
    editBattery: "Edit Battery",
    model: "Model",
    serialNumber: "Serial Number",
    firmwareVersion: "Firmware Version",
    registrationNumber: "Registration Number",
    lastMaintenance: "Last Maintenance",
    serviceInterval: "Service Interval (hours)",
    flightHours: "Flight Hours",
    serviceCycle: "Service Cycle",
    cycleCount: "Cycle Count",
    cycleLimit: "Cycle Limit",
    status: "Status",
    operational: "Operational",
    maintenanceSoon: "Maintenance Soon",
    maintenanceRequired: "Maintenance Required",
    aircraftMaintenanceAlert: "aircraft need immediate maintenance",
    aircraftMaintenanceSoonAlert: "aircraft need maintenance soon",
    searchAircraft: "Search aircraft...",
    noAircraftFound: "No aircraft found",
    unregistered: "Unregistered",
    assignedAircraft: "Assigned Aircraft",
    cycleUsage: "Cycle Usage",
    good: "Good",
    warning: "Warning",
    replace: "Replace",
    batteryReplaceAlert: "battery(s) need replacement",
    batteryApproachingAlert: "battery(s) approaching limit",
    searchBatteries: "Search batteries...",
    noBatteriesFound: "No batteries found",
    selectAircraftOptional: "Select aircraft (optional)",
    // Aircraft Documents
    documents: "Documents",
    registration: "Registration",
    maintenance: "Maintenance",
    inspection: "Inspection",
    technicalCertificate: "Technical Certificate",
    noDocuments: "No documents uploaded",
    uploadHint: "Upload registration, maintenance records, or inspection documents",
    // SORA Equipment
    soraEquipment: "SORA Equipment",
    soraEquipmentHint: "Select installed safety equipment for SORA pre-assessment",
    parachute: "Parachute",
    fts: "Flight Termination System (FTS)",
    geoAwareness: "Geo-awareness",
    remoteId: "Remote ID",
    // Remote ID Compliance
    remoteIdSection: "Remote ID Compliance",
    remoteIdSerial: "RID Serial Number",
    remoteIdType: "Remote ID Type",
    remoteIdCompliant: "RID Verified",
    remoteIdTypes: {
      built_in: "Built-in",
      add_on: "Add-on Module",
      broadcast_only: "Broadcast Only",
      none: "None"
    },
    remoteIdStatus: {
      compliant: "Compliant",
      nonCompliant: "Non-compliant",
      unknown: "Unknown"
    },
    // Block I-2: Remote ID icon tooltips
    ridIcon: {
      header: "Remote ID",
      builtIn: "Built-in",
      external: "External module",
      compliant: "Compliant",
      exempted: "Exempted",
      nonCompliant: "Non-compliant",
      unknown: "Unknown"
    },
    remoteIdWarning: "Drone does not have verified Remote ID — check before flight",
    // NEW: MTOM and UA Class
    mtomKg: "MTOM (kg)",
    maxSpeedMs: "Max. speed (m/s)",
    maxSpeedHelper: "Manufacturer-declared maximum speed (m/s) — required, an input of the SORA 2.5 risk calculation.",
    maxSpeedHelperRecommended: "Manufacturer-declared maximum speed (m/s) — recommended to add, an input of the SORA 2.5 risk calculation.",
    maxSpeedMissing: "Max. speed missing",
    maxSpeedMissingHint: "The manufacturer-declared maximum speed of this aircraft is missing — SORA only uses a simplified MTOM/altitude estimate for it. Add it by editing the aircraft.",
    maxSpeedMissingDialogHint: "The manufacturer-declared maximum speed of this aircraft is missing — SORA only uses a simplified MTOM/altitude estimate for it. The aircraft can be saved without it.",
    mtomHelper: "Maximum take-off mass including all payload — required, it sets the UA class and is an input of the SORA calculation.",
    mtomHelperRecommended: "Maximum take-off mass including all payload — recommended to add, it sets the UA class and is an input of the SORA calculation.",
    mtomMissing: "MTOM missing",
    mtomMissingHint: "The maximum take-off mass (MTOM) of this aircraft is missing — its UA class cannot be determined and SORA uses a conservative 25 kg estimate for it. Add it by editing the aircraft.",
    mtomMissingDialogHint: "The maximum take-off mass (MTOM) of this aircraft is missing — its UA class cannot be determined and SORA uses a conservative 25 kg estimate for it. The aircraft can be saved without it.",
    soraInputsMissing: "SORA data missing",
    soraInputsMissingHint: "Both the maximum take-off mass (MTOM) and the manufacturer-declared maximum speed of this aircraft are missing — its UA class cannot be determined and SORA only uses simplified estimates for it. Add them by editing the aircraft.",
    mtomPlaceholder: "e.g. 2.5",
    uaCategoryEnterMtom: "Enter MTOM",
    formGroupIdentification: "Identification",
    formGroupTechnical: "Technical parameters",
    formGroupClass: "Category and marking",
    formGroupMaintenance: "Maintenance and operation",
    legacyDrone: "Drone without C0-C6 class marking",
    legacyDroneHelper: "Older or custom-built drone without EU class marking",
    uaCategory: "UA Category",
    noClassMarking: "No class marking",
    modelRequired: "Aircraft model is required.",
    serialNumberRequired: "Serial number is required.",
    maxSpeedRequired: "The manufacturer-declared maximum speed is required.",
    maxSpeedPositive: "Maximum speed must be a positive number of m/s.",
    mtomRequired: "The maximum take-off mass (MTOM) is required.",
    mtomPositive: "MTOM must be a positive number of kg.",
    // SORA characteristic dimension (Table 2 iGRC input)
    characteristicDimensionSection: "SORA characteristic dimension",
    characteristicDimensionSectionSubtitle: "Used by the quantitative SORA 2.5 ground-risk table",
    maxCharacteristicDimensionM: "Maximum characteristic dimension (m)",
    maxCharacteristicDimensionHelper: "Wingspan (fixed-wing), main rotor diameter (helicopter), or the farthest distance between rotor blade tips (multirotor) — an input for the more detailed SORA 2.5 risk calculation.",
    maxCharacteristicDimensionHelpTitle: "How to measure the characteristic dimension",
    maxCharacteristicDimensionRequired: "Required for new aircraft — used by the SORA 2.5 ground-risk table.",
    maxCharacteristicDimensionPositive: "Maximum characteristic dimension must be a positive number of metres.",
    maxCharacteristicDimensionLockedHelper: "Already recorded for this aircraft — clearing it isn't supported here to avoid losing a SORA-relevant value.",
    diagramFixedWing: "Fixed-wing — wingspan",
    diagramHelicopter: "Helicopter — main rotor diameter",
    diagramMultirotor: "Multirotor — farthest blade-tip distance",
    detectedFromCatalog: "Detected from the drone catalog: {type}",
    selectAircraftType: "Select the aircraft's construction to see the matching diagram:",
    vtolAmbiguousHelper: "This VTOL aircraft could be built around either fixed-wing or multirotor geometry — pick the one that matches its actual construction:",
    droneTypes: {
      fixed_wing: "Fixed-wing",
      helicopter: "Helicopter",
      multirotor: "Multirotor",
      vtol: "VTOL",
      other: "Other"
    },
    // Filter chips (Block I-1)
    filterChips: {
      all: "All",
      operational: "Operational",
      maintenanceSoon: "Maintenance Soon",
      maintenanceRequired: "Maintenance Required"
    },
    // Status tooltip (Block M-3)
    statusTooltip: {
      required: "Maintenance Required",
      soon: "Maintenance Due Soon",
      operational: "Operational",
      hoursReason: "Flight hours: {current}/{limit} h",
      calendarReason: "Calendar: {current}/{limit} days",
      missingDate: "Last maintenance date missing",
      bothAxes: "Both thresholds exceeded"
    },
    // Service bar units (Block M-3 update)
    serviceBarUnits: {
      hours: "h",
      days: "d"
    },
  },
  
  // Safety / Incidents
  safety: {
    title: "Safety Management",
    incidents: "Incidents",
    reportIncident: "Report Incident",
    editIncident: "Edit Incident",
    incidentDate: "Incident Date",
    classification: "Classification",
    severity: "Severity",
    description: "Description",
    correctiveActions: "Corrective Actions",
    correctiveActionsIfAny: "Corrective Actions (if any)",
    reportingRequired: "Reporting required to authorities",
    missionReference: "Mission Reference",
    equipmentInvolved: "Equipment Involved",
    equipmentType: "Equipment Type",
    selectEquipmentType: "Select equipment type",
    notEquipmentRelated: "Not equipment related",
    aircraftInvolved: "Aircraft Involved",
    batteryInvolved: "Battery Involved",
    selectEquipment: "Select equipment",
    noneSelected: "None selected",
    selectMission: "Select mission (optional)",
    noLinkedMission: "No linked mission",
    selectMissionById: "Select Mission by ID",
    attachments: "Attachments",
    uploadFiles: "Upload Files",
    filesDescription: "Images, PDFs, Documents",
    resolve: "Resolve",
    resolveIncident: "Resolve Incident",
    open: "Open",
    resolved: "Resolved",
    minor: "Minor",
    serious: "Serious",
    low: "Low",
    medium: "Medium",
    high: "High",
    critical: "Critical",
    openIncidentsAlert: "open incident(s)",
    seriousIncidentsAlert: "serious incident(s)",
    searchIncidents: "Search incidents...",
    noIncidentsFound: "No incidents found",
    descriptionPlaceholder: "Describe what happened, circumstances, actions taken, potential causes...",
    correctiveActionsPlaceholder: "Describe any corrective actions taken or planned...",
    resolvePlaceholder: "Describe the corrective actions taken to resolve this incident...",
    other: "Other Equipment",
    noAttachments: "No attachments",
    // Incident types (Wave 3 Block 3C)
    incidentType: "Incident Type",
    selectIncidentType: "Select incident type",
    reportableToUcl: "CAA Reporting Required",
    reportableToUclHint: "This incident must be reported to the Civil Aviation Authority",
    incidentTypes: {
      equipment_failure: "Equipment Failure",
      collision_risk: "Collision Risk",
      loss_of_control: "Loss of Control",
      airspace_violation: "Airspace Violation",
      pilot_error: "Pilot Error",
      environmental: "Environmental",
      flyaway: "Flyaway",
      injury: "Personal Injury",
      property_damage: "Property Damage",
      other: "Other"
    },
    // Photos (Wave 3 Block 3C)
    photos: "Photo Documentation",
    uploadPhoto: "Upload Photo",
    noPhotos: "No photos",
    photoMetadata: "Photo Metadata",
    exifLocation: "GPS Coordinates",
    exifTimestamp: "Capture Time",
    exifCamera: "Camera",
    photoSize: "Size",
    maxPhotoSize: "Max. 10 MB",
    imagesOnly: "Only images allowed",
    uploadFailed: "Upload failed",
    attachmentUploadFailed: "The incident was saved, but the attachment \"{name}\" could not be uploaded.",
    photoDeleted: "Photo deleted",
    photoDeleteError: "Delete failed",
    // Comments (Wave 3 Block 3C)
    comments: "Comments",
    addComment: "Add Comment",
    addCommentError: "Failed to add comment",
    noComments: "No comments",
    commentPlaceholder: "Write a comment...",
    deleteComment: "Delete Comment",
    commentDeleted: "Comment deleted",
    commentDeleteError: "Delete failed",
  },
  
  // Compliance Widget (Wave 3 Block 3C)
  complianceWidget: {
    title: "Compliance Status",
    subtitle: "Fleet and crew status overview",
    pilotsSection: "Pilots",
    aircraftSection: "Aircraft",
    batteriesSection: "Batteries",
    incidentsSection: "Incidents",
    valid: "Valid",
    operational: "Operational",
    good: "Good",
    expiring: "Expiring",
    maintenanceDue: "Maintenance Due",
    warning: "Warning",
    expired: "Expired",
    grounded: "Grounded",
    replace: "Replace",
    openIncidents: "Open",
    seriousOpen: "Serious Open",
    reportableOpen: "CAA Reportable",
    overallHealthy: "All Clear",
    overallWarning: "Attention Required",
    overallCritical: "Critical Issues",
    criticalIssues: "Critical Issues",
    warnings: "Warnings",
    viewDetails: "View Details",
  },
  complianceModule: {
    title: "Compliance Management",
    scenarios: "Operational Scenarios",
    documents: "Documents",
    documentation: "Documentation",
    addScenario: "Add Scenario",
    editScenario: "Edit Scenario",
    addDocument: "Add Document",
    editDocument: "Edit Document",
    scenarioName: "Scenario Name",
    scenarioNamePlaceholder: "e.g., Pipeline Inspection - VLOS",
    operationType: "Operation Type",
    riskMitigations: "Key Risk Mitigations",
    riskMitigationsPlaceholder: "Describe risk mitigation measures...",
    requiredAuth: "Required Authorization",
    documentType: "Document Type",
    documentName: "Document Name",
    documentNamePlaceholder: "e.g., Operations Manual v3.2",
    documentVersion: "Version",
    approvalDate: "Approval Date",
    lastReview: "Last Review",
    active: "Active",
    inactive: "Inactive",
    searchScenarios: "Search scenarios...",
    noScenariosFound: "No scenarios found",
    searchDocuments: "Search documents...",
    noDocumentsFound: "No documents found",
    documentsExpiredAlert: "document(s) have expired",
    documentsExpiringAlert: "document(s) expiring soon",
    selectType: "Select type",
  },
  
  // Required Documents
  requiredDocs: {
    title: "Required Documents",
    operationsManual: "Operations Manual",
    conops: "ConOps",
    sora: "SORA",
    internalProcedures: "Internal Procedures",
    available: "Available",
    missing: "Missing",
    expired: "Expired",
    valid: "Valid",
    expiring: "Expiring",
    unknown: "Unknown",
  },
  
  // Validation messages
  validation: {
    required: "This field is required",
    invalidEmail: "Invalid email address",
    selectPilot: "Please select a pilot",
    selectAircraft: "Please select an aircraft",
    selectDate: "Please select a date",
    enterLocation: "Please enter a location",
    maxAltitudeRange: "Enter a max. flight altitude between 1 and 500 m AGL",
    enterName: "Please enter a name",
    enterPilotName: "Please enter a pilot name",
    enterDescription: "Please provide a description",
    selectIncidentDate: "Please select an incident date",
    provideIncidentDescription: "Please provide an incident description",
    provideCorrectiveActions: "Please provide corrective actions",
    enterScenarioName: "Please enter a scenario name",
    selectOperationType: "Please select an operation type",
  },
  
  // Success messages
  messages: {
    saved: "Saved successfully",
    deleted: "Deleted successfully",
    updated: "Updated successfully",
    created: "Created successfully",
    uploaded: "File uploaded successfully",
    checklistSaved: "Checklist saved successfully",
    incidentResolved: "Incident resolved",
    missionLogged: "Mission logged successfully",
    missionUpdated: "Mission updated successfully",
    missionDeleted: "Mission deleted",
    pilotCreated: "Pilot created successfully",
    pilotUpdated: "Pilot updated successfully",
    pilotDeleted: "Pilot deleted",
    aircraftAdded: "Aircraft added successfully",
    aircraftUpdated: "Aircraft updated successfully",
    aircraftDeleted: "Aircraft deleted",
    noChanges: "No changes to save",
    batteryAdded: "Battery added successfully",
    batteryUpdated: "Battery updated successfully",
    batteryDeleted: "Battery deleted",
    scenarioCreated: "Scenario created successfully",
    scenarioUpdated: "Scenario updated successfully",
    scenarioDeleted: "Scenario deleted",
    documentAdded: "Document added successfully",
    documentUpdated: "Document updated successfully",
    documentDeleted: "Document deleted",
    incidentReported: "Incident reported successfully",
    incidentUpdated: "Incident updated successfully",
    incidentDeleted: "Incident deleted",
    fetchError: "Failed to fetch data",
    saveError: "Failed to save",
    deleteError: "Failed to delete",
  },

  // Dialog Titles
  dialogs: {
    addMission: "Log New Mission",
    editMission: "Edit Mission",
    addPilot: "Add Pilot",
    editPilot: "Edit Pilot",
    addAircraft: "Add Aircraft",
    editAircraft: "Edit Aircraft",
    addBattery: "Add Battery",
    editBattery: "Edit Battery",
    addScenario: "Add Scenario",
    editScenario: "Edit Scenario",
    addDocument: "Add Document",
    editDocument: "Edit Document",
    reportIncident: "Report Incident",
    editIncident: "Edit Incident",
    resolveIncident: "Resolve Incident",
    confirmDelete: "Confirm Delete",
  },

  // Placeholders
  placeholders: {
    searchMissions: "Search...",
    searchPilots: "Search pilots...",
    searchAircraft: "Search aircraft...",
    searchBatteries: "Search batteries...",
    searchScenarios: "Search scenarios...",
    searchDocuments: "Search documents...",
    searchIncidents: "Search incidents...",
    selectOrganization: "Select organization",
    locationExample: "e.g., Wind Farm Alpha",
    registrationExample: "EU-REG-XXXX-XXXX",
  },

  // Alerts
  alerts: {
    history: "Alert History",
    historyDescription: "View and track all compliance alerts sent by the system",
    totalAlerts: "Total Alerts",
    sent: "Sent",
    failed: "Failed",
    dryRun: "Dry Run",
    skipped: "Skipped",
    scheduler: "Scheduler",
    runCheck: "Run Compliance Check",
    alertType: "Alert Type",
    severity: "Severity",
    status: "Status",
    entity: "Entity",
    recipient: "Recipient",
    timestamp: "Timestamp",
    startDate: "Start Date",
    endDate: "End Date",
    noAlerts: "No alerts found",
    searchPlaceholder: "Search alerts...",
    alertLog: "Alert Log",
    loadError: "Failed to load alert history",
    runCheckError: "Failed to run compliance check",
    checkComplete: "Compliance check completed. {count} alerts processed.",
  },

  // Audit
  audit: {
    title: "Compliance Audit Simulation",
    description: "Simulate regulatory compliance audits and identify operational weaknesses",
    runAudit: "Run Audit",
    results: "Audit Results",
    history: "Audit History",
    ready: "AUDIT READY",
    attentionRequired: "ATTENTION REQUIRED",
    complianceScore: "Compliance Score",
    passed: "Passed",
    warnings: "Warnings",
    critical: "Critical",
    auditId: "Audit ID",
    downloadPackage: "Download Audit Package",
    findingsByCategory: "Findings by Category",
    findingsDescription: "Detailed breakdown of compliance issues found during the audit",
    pilotCertifications: "Pilot Certifications",
    aircraftMaintenance: "Aircraft Maintenance",
    batterySafety: "Battery Safety",
    documentation: "Documentation",
    missionCompliance: "Mission Compliance",
    incidentStatus: "Incident Status",
    scenarioAssignments: "Scenario Assignments",
    checked: "checked",
    entity: "Entity",
    severity: "Severity",
    issue: "Issue",
    suggestedAction: "Suggested Action",
    noIssues: "No issues found in this category",
    noAuditRun: "No Audit Run Yet",
    clickToRun: "Click 'Run Audit' to simulate a compliance audit for the selected organization",
    auditHistory: "Audit History",
    historyDescription: "View past audit runs and download audit packages",
    score: "Score",
    status: "Status",
    issues: "Issues",
    noHistory: "No audit history found",
    // CSV Import audit entries
    filterAll: "All",
    filterAuditSimulations: "Audit Simulations",
    filterCsvImports: "CSV Imports",
    filterDataExports: "Data Exports",
    actionAudit: "Audit Simulation",
    actionCsvImport: "CSV Import",
    actionDataExport: "Data Export",
    sourceFile: "Source File",
    importedRecords: "Imported",
    skippedRecords: "Skipped",
    failedRecords: "Failed",
    selectOrgError: "Please select an organization",
    auditComplete: "Audit completed: {id}",
    runAuditError: "Failed to run audit",
    packageDownloaded: "Audit package downloaded",
    downloadError: "Failed to download audit package",
    detailLoadError: "Failed to load audit detail",
  },

  // Reports & Data page
  reports: {
    fetchOrgsError: "Failed to fetch organizations",
    selectOrgError: "Please select an organization",
    scopeRequired: "Select a specific organization for this action. CSV export remains available for all organizations.",
    scopeChangeConfirm: "You have an unfinished CSV import. Changing organization will clear the pasted content. Continue?",
    reportDownloaded: "Report downloaded successfully",
    reportError: "Failed to generate report",
    templateDownloaded: "Template downloaded",
    templateError: "Failed to download template",
    exportSuccess: "Data exported successfully",
    exportError: "Failed to export data",
    pasteCsvError: "Please paste CSV content",
    importSuccess: "Imported {count} records",
    importFailedCheckErrors: "Import failed - check errors below",
    importError: "Import failed",
    alertsSent: "Sent {count} alert(s)",
    emailNotConfigured: "Email service not configured. Add RESEND_API_KEY to enable.",
    alertsError: "Failed to send alerts",
  },

  // CSV Import
  import: {
    title: "Import from CSV",
    subtitle: "Bulk import data from CSV file",
    scopeChangeConfirm: "The import is still running on the server. Changing organization will close this dialog and stop progress tracking, but the import will continue on the server. Change organization anyway?",
    // Steps
    step1: "Upload File",
    step2: "Preview & Validate",
    step3: "Result",
    stepOf: "Step {current} of {total}",
    // Dropzone
    dropzoneTitle: "Drop CSV file here",
    dropzoneSubtitle: "or click to select file",
    dropzoneHint: "Supported: UTF-8, CP1250 | Separators: comma, semicolon",
    downloadTemplate: "Download Template",
    selectFile: "Select File",
    selectedFile: "Selected File",
    fileSize: "Size",
    changeFile: "Change File",
    uploadAndValidate: "Upload & Validate",
    // Preview
    previewTitle: "Import Preview",
    summary: "Summary",
    totalRows: "Total Rows",
    validRows: "Valid",
    invalidRows: "Invalid",
    duplicateRows: "Duplicates",
    warningRows: "Warnings",
    previewTable: "Data Preview (first 50 rows)",
    rowNumber: "Row",
    rowStatus: "Status",
    rowData: "Data",
    // Row statuses
    statusValid: "Valid",
    statusInvalid: "Invalid",
    statusDuplicate: "Duplicate",
    statusWarning: "Warning",
    // Validation settings
    validationSettings: "Validation Settings",
    skipInvalidRows: "Skip invalid rows",
    skipInvalidRowsHint: "{count} rows will be skipped",
    // Duplicate handling
    duplicateHandling: "Duplicate handling",
    duplicateModeSkip: "Skip duplicates",
    duplicateModeUpdate: "Update existing",
    duplicateModeFail: "Stop on duplicate",
    duplicateOf: "Duplicate of",
    // Buttons
    back: "Back",
    import: "Import",
    importCount: "Import ({count})",
    cancel: "Cancel",
    done: "Done",
    close: "Close",
    // Progress
    progressTitle: "Import in progress",
    progressSubtitle: "Please wait while data is being imported...",
    processingRow: "Row {current} of {total}",
    estimatedTime: "Estimated remaining",
    cancelImport: "Cancel Import",
    cancelWarning: "Note: Already imported rows will not be rolled back.",
    // Result
    resultTitle: "Import Complete",
    resultSuccess: "Import completed successfully",
    resultPartial: "Import completed with errors",
    resultFailed: "Import failed",
    resultCancelled: "Import cancelled",
    imported: "Imported",
    skipped: "Skipped",
    failed: "Failed",
    updated: "Updated",
    downloadErrors: "Download Errors (CSV)",
    errorsTitle: "Row errors ({count})",
    errorsField: "Field",
    errorsValue: "Value",
    errorsMessage: "Error",
    errorsShownOf: "Showing the first {shown} of {total} errors — download the CSV for the full list.",
    viewInList: "View in List",
    importAnother: "Import Another",
    noValidRows: "No valid rows to import",
    // Errors
    fileTooLarge: "File is too large (max 5 MB)",
    invalidFileType: "Invalid file type (expected .csv)",
    tooManyRows: "Too many rows (max 5000)",
    uploadError: "Error uploading file",
    previewExpired: "Preview expired, please upload file again",
    importInProgress: "Another import is already in progress",
    importCancelled: "Import cancelled",
    // Refused confirm (shown at the preview step); {count} = duplicates in the file
    uploadAgain: "Upload the file again",
    confirmErrors: {
      previewExpired: "The preview has expired (it is valid for 15 minutes). Upload the file again.",
      anotherImportInProgress: "Another import is already running for this organization. Wait for it to finish and try again.",
      duplicateFound: "The file contains duplicate records ({count}) and “Stop on duplicate” is selected. Choose another duplicate handling or remove the duplicates from the file.",
      forbidden: "You are not allowed to confirm this import.",
      serverError: "Server error. The import may not have run in full — check the list before you repeat it.",
      networkError: "Could not reach the server. Before you try again, check the list in case the data was already imported.",
      confirmFailed: "The import could not be started.",
    },
    // Error codes (for backend error mapping)
    errors: {
      requiredField: "Required field",
      invalidEmail: "Invalid email",
      invalidDate: "Invalid date format",
      invalidNumber: "Invalid number",
      negativeNumber: "Number cannot be negative",
      positiveRequired: "Number must be positive",
      valueTooLong: "Value is too long",
      duplicateFound: "Duplicate found",
      invalidDocumentType: "Invalid document type",
      unknownField: "Unknown field",
      serverError: "Server error",
      quotaExceeded: "Organization quota exceeded",
      invalidCsvContent: "Invalid CSV content",
      encodingError: "File encoding error",
      fileTooLarge: "File too large",
      tooManyRows: "Too many rows",
      invalidFileType: "Invalid file type",
      anotherImportInProgress: "Another import in progress",
      previewExpired: "Preview expired",
      forbidden: "Access denied",
      serverRestart: "Import interrupted (server restarted). Some rows may have been imported — check the list.",
      jobStatusUnavailable: "The import status could not be retrieved. The import may have finished on the server in full or only in part — check the list.",
    },
    // Permissions
    noPermission: "You do not have permission to import data. Contact your organization administrator.",
    // Entity types
    entityTypes: {
      pilots: "pilots",
      aircraft: "aircraft",
      batteries: "batteries",
      documents: "documents",
    },
  },

  // SORA Pre-Assessment
  sora: {
    title: "SORA Pre-Assessment",
    environment: "Environment Type",
    rural: "Rural / Sparsely Populated",
    urban: "Urban / Populated",
    
    // Core outputs
    estimatedSail: "Estimated SAIL Level (Pre-Assessment Only)",
    sailLevel: "Estimated SAIL Level",
    riskLevel: "Risk Level",
    low: "Low",
    medium: "Medium",
    high: "High",
    
    // GRC / ARC
    grc: "Ground Risk Class (GRC)",
    arc: "Air Risk Class (ARC)",
    groundRisk: "Ground Risk",
    airRisk: "Air Risk",
    mitigated: "after mitigation",
    reducedFrom: "Reduced from",
    throughMitigations: "through applied mitigations",
    baseRiskTooltip: "Base risk ({0}) was reduced by {1} level(s) due to:",
    
    // ARC Descriptions
    arcAtypical: "Atypical airspace with managed traffic",
    arcLow: "Low probability of encounter with manned aircraft",
    arcMedium: "Medium probability of encounter with manned traffic",
    arcHigh: "High encounter probability; significant mitigations required",
    
    // Sections
    inputsUsed: "Assessment Input Parameters",
    riskDrivers: "Factors Affecting Risk Level",
    riskFactors: "Risk Increasing Factors",
    mitigationsApplied: "Applied Mitigations",
    uncertainty: "Assessment Uncertainty",
    uncertaintyFactors: "Uncertainty Factors",
    
    // Confidence
    confidence: "Assessment Confidence",
    confidenceHigh: "High Confidence",
    confidenceMedium: "Medium Confidence",
    confidenceLow: "Low Confidence",
    confidenceReliability: "Assessment Reliability",
    moreCount: "+{count} more",
    
    // Mitigations grouped
    strategic: "Strategic Measures (M1)",
    technical: "Technical Measures (M2)",
    mitigationsStatus: "UA Mitigation Measures",
    
    // SORA 2.5 Mitigation categories
    m1a: "Sheltering (M1A) – people protected by structures",
    m1b: "Operational Restrictions (M1B) – defined operational area",
    m1c: "Ground Observation (M1C) – visual route monitoring",
    m2: "Technical Mitigation (M2) – parachute, FTS",
    
    // Containment (SORA 2.5)
    containmentLevel: "Containment Level",
    containmentRequired: "Containment Required",
    containmentNone: "Not Required",
    containmentLow: "Low",
    containmentMedium: "Medium",
    containmentHigh: "High",
    
    // SORA version
    soraVersion: "SORA Version",
    soraNote: "Assessment per SORA 2.5",
    sora25Disclaimer: "Simplified pre-assessment per JARUS SORA 2.5 (mandatory from 29.9.2025 per ED Decision 2025/018/R). Does not replace formal SORA analysis.",
    
    // Max speed
    maxSpeed: "Max Speed (m/s)",
    maxSpeedHelper: "Manufacturer-declared maximum speed of UA – an input of the GRC calculation per SORA 2.5",
    
    // Mitigation names (SORA 2.5)
    parachuteRecovery: "Parachute System (M2)",
    flightTermination: "Flight Termination System – FTS (M2)",
    geoAwareness: "Geo-awareness (M1B)",
    remoteId: "Remote Identification (Remote ID)",
    visualLineOfSight: "VLOS Operation",
    groundObservation: "Ground Observation (M1C)",
    sheltering: "Sheltering (M1A)",
    
    // Missing / Recommendations
    missingRequirements: "Missing / Recommended Measures",
    recommendations: "Recommended Actions",
    
    // Operational categories
    openSpecificLow: "Open/Specific (Low Risk)",
    specificStandard: "Specific (Standard Authorization)",
    specificEnhanced: "Specific (Enhanced Authorization) / Certified",
    
    // Assessment inputs
    assessmentInputs: "Input Parameters",
    assessmentBasis: "Assessment Basis",
    operationType: "Operation Type",
    locationStatus: "Location",
    defined: "Defined",
    undefined: "Not Defined",
    precision: "precision",
    aircraft: "UA",
    
    // Disclaimers (SORA 2.5)
    disclaimer: "This is a simplified pre-assessment per SORA 2.5 and does NOT replace a formal SORA evaluation.",
    disclaimerShort: "Pre-assessment per SORA 2.5. Does not replace official analysis.",
    noAssessment: "Unable to perform assessment. Ensure UA is assigned.",
    
    // Summary sentence parts
    summaryEstimated: "Estimated SAIL Level",
    summaryDueTo: "due to",
    summaryUrbanEnv: "urban environment",
    summaryRuralEnv: "rural environment", 
    summaryBvlos: "BVLOS operation",
    summaryAirportProximity: "proximity to",
    summaryMitigationsReduce: "Mitigations reduce ground risk",
    summaryMitigationsApplied: "mitigations applied",
    
    // Data source indicators
    dataSourceLive: "Live airspace data",
    dataSourceStatic: "Static data (offline mode)",
    dataSourceTooltip: "OpenAIP API unavailable – using simplified airport database",
    
    // Legacy drone warnings (priority-based)
    legacyHighRiskNoRemoteId: "⚠️ UA without C0-C6 class marking and no Remote ID in high-risk airspace – this combination requires attention before starting the operation. We recommend consultation with the relevant authority or info@aeroway.cz",
    legacyUrbanNoRemoteId: "⚠️ UA without C0-C6 class marking and no Remote ID in urban environment – operation may require special authorization. Verify the situation in DroneMap or contact the relevant authority. Need help with authorization or operation analysis? Our team is happy to help – info@aeroway.cz",
    legacyUrban: "⚠️ UA without C0-C6 class marking in urban environment – operation may require special authorization. Verify the situation in DroneMap or contact the relevant authority. Need help with authorization or operation analysis? Our team is happy to help – info@aeroway.cz",
    legacyRuralInfo: "ℹ️ UA without C0-C6 class marking – in sparsely populated areas generally no restrictions, have UA documentation available. For clarifications, contact info@aeroway.cz",
    
    // PDF Export
    exportPdf: "Export SORA PDF",
    pdfGenerated: "SORA PDF generated",
    
    // Risk factor translations (from backend)
    riskAirspaceAssessmentCaution: "Airspace assessment: caution",
    riskAirspaceAssessmentHighRisk: "Airspace assessment: high risk",
    riskLocationPrecisionLow: "Location precision is low - verify exact mission area",
    riskUrbanEnvironment: "Urban environment increases ground risk",
    riskBvlosOperation: "BVLOS operation increases air risk",
    riskProximityToAirport: "Proximity to {airport} ({distance} km)",
    
    // Mitigation translations (from backend)
    mitParachuteReduces: "Parachute system (M2) reduces ground impact severity",
    mitFtsControlled: "FTS (M2) enables controlled flight termination",
    mitGeoAwarenessPrevents: "Geo-awareness (M1B) prevents airspace boundary violations",
    mitRemoteIdEnhances: "Remote ID enhances situational awareness",
    
    // Uncertainty translations (from backend)
    uncLocationDefined: "Location defined but low precision",
    uncNoLocationCoordinates: "No location coordinates provided",
    
    // Confidence reason translations
    plusMore: "+{count} more",
  },

  // Airspace Advisory
  airspace: {
    title: "Airspace Advisory",
    highRisk: "High Risk",
    caution: "Caution",
    noIssues: "No Known Issues",
    unknown: "Unknown",
    reasons: "Assessment",
    recommendations: "Recommendations",
    disclaimer: "This is a simplified advisory and does not replace official airspace verification (e.g., DroneMap).",
    noAdvisory: "Set coordinates to enable airspace assessment",
    nearestAirport: "Nearest Airport",
    distanceKm: "km away",
    confidence: "Confidence",
    locationPrecision: "Location Precision",
  },

  // Map Layers
  map: {
    pointsOfInterest: "Points of Interest",
    pointsOfInterestDescription: "Hospitals, prisons, nuclear facilities, government buildings",
    verifyRestrictions: "Verify local restrictions before flight",
    informationalNote: "Informational display - does not replace official sources",
    distance: "Distance",
    categories: {
      hospital: "Hospital",
      prison: "Prison",
      nuclear: "Nuclear Facility",
      government: "Government Building",
      military: "Military Area"
    },
    legend: {
      title: "Map Layers",
      prohibited: "Prohibited (incl. LKP)",
      poi: "Points of Interest"
    }
  },

  // Mission Lifecycle
  lifecycle: {
    status: "Status",
    draft: "Draft",
    ready: "Ready",
    active: "Active",
    completed: "Completed",
    planning: "Planning",
    activeToday: "Active (Today)",
  },

  // Daily Ops Dashboard
  dailyOps: {
    title: "Daily Ops Dashboard",
    subtitle: "Overview of today's operations",
    totalToday: "Total Today",
    todaysMissions: "Today's Missions",
    operationalAlerts: "Operational Alerts",
    quickActions: "Quick Actions",
    viewAllMissions: "View All Missions",
    viewHistory: "Mission History",
    noMissionsToday: "No missions scheduled for today",
    noAlerts: "No operational alerts",
    complianceIssues: "Compliance Issues",
    markCompleted: "Mark as Completed",
    // New polish pass translations
    needsAttention: "Needs Attention",
    missionsNeedWork: "missions need work",
    allClear: "all clear",
    readyToFly: "Ready to Fly",
    missionsReady: "missions ready or active",
    completedToday: "completed today",
    actionRequired: "Action Required",
    fix: "Fix",
    viewAllIssues: "View all issues",
    more: "more",
    activeAndReady: "Active & Ready",
    inProgress: "In Progress",
    readyToStart: "Ready to Start",
    noReadyMissions: "No missions ready to fly",
    prepareMission: "Prepare a mission",
    noDrafts: "No missions in planning",
    scheduleHint: "Schedule a mission to get started",
    noPilot: "No pilot assigned",
    noAircraft: "No aircraft assigned",
    unassigned: "Unassigned",
    blockers: "Blocking",
    missionCompleted: "Mission marked as completed",
  },

  // Mission Detail - Polish pass
  missionDetail: {
    blockedBy: "This mission cannot be marked ready due to:",
    fixIssues: "Fix Issues",
    checklistIncomplete: "Checklist incomplete",
  },

  // Mission History
  missionHistory: {
    title: "Mission History",
    subtitle: "View completed and past missions",
    searchPlaceholder: "Search missions...",
    filterByMonth: "Filter by month",
    filterByOrg: "Filter by organization",
    missionsFound: "missions found",
    noHistory: "No mission history found",
  },

  // Mission Calendar
  missionCalendar: {
    title: "Mission Calendar",
    subtitle: "Visual overview of scheduled missions",
    today: "Today",
    more: "more",
    noMissions: "No missions scheduled",
    clickToView: "Click to view details",
  },

  // None option
  none: "None",
  
  // U-Space fields
  uSpace: {
    operatorId: "EASA Operator ID",
    operatorIdHelper: "Operator registration number from EASA/CAA registry (e.g., CZE-OP-XXXXXX)",
    sectionTitle: "U-Space & Flight Plan",
    sectionSubtitle: "Optional data for U-Space zone operations",
    etot: "Planned Take-off Time (ETOT)",
    etotHelper: "Estimated Time of Take-Off – required for U-Space operations",
    eldt: "Planned Landing Time (ELDT)",
    eldtHelper: "Estimated Landing Time",
    uSpaceZone: "U-Space Zone",
    uSpaceAuthorization: "U-Space Authorization",
    uSpaceNotRequired: "Not Required",
    uSpaceReadyToSubmit: "Ready to Submit",
    uSpaceApiNotConnected: "API Not Connected",
    uSpaceReadiness: "U-Space Readiness",
    uSpaceReadinessLow: "Low Readiness",
    uSpaceReadinessMedium: "Medium Readiness",
    uSpaceReadinessHigh: "High Readiness",
  },
  
  // SAIL Status Banner
  sailBanner: {
    openCategory: "Open category",
    specificAuthorization: "Specific authorization",
    whySailLevel: "Why SAIL",
    insideCtr: "Inside CTR",
    elevatedAirspaceRisk: "Elevated airspace risk",
    nearAirport: "Near airport",
    urbanArea: "Urban / populated area",
    bvlosOperation: "BVLOS operation",
    highGrc: "High ground risk",
    openCategoryStatus: "Open category / STS scenario",
    specificRequired: "Requires CAA Specific authorization",
    openCheckMate: "Open CheckMate",
    calculating: "Calculating...",
    assessmentInProgress: "Assessment in progress...",
  },
  
  // ARC by SORA 2.5 Figure 6 over the ŘLP UAS zones (banner, provisional box, "Airspace" card,
  // CheckMate, environment on the mission form)
  arcDisplay: {
    airspaceUndetermined: "Airspace cannot be determined — the worst-case ARC is used",
    highRiskAirportCtr: "Airport environment in controlled airspace (CTR/MCTR)",
    highRiskAbove500Ctr: "Above 500 ft AGL in a CTR/MCTR",
    highRiskAbove1000: "Above 1000 ft AGL (class E controlled airspace)",
    withoutDeclaration: "{reason} (without the ARC-a declaration)",
    atsCoordination: "Inside CTR/MCTR, coordination with ATS is likely required (telephone or continuous radio contact).",
    atcAgreement: "In ARC-d airspace an agreement with ATC may be required (SORA 2.5 §4.5.4).",
    tmpr: "BVLOS — tactical mitigation performance requirement (TMPR, SORA 2.5 Table 6): {level}",
    tmprLevels: { high: "High", medium: "Medium", low: "Low", none: "None" },
    provisionalAirspaceTitle: "Airspace cannot be determined — the worst-case ARC is used",
    provisionalAtypicalAcceptedTitle: "ARC-a from the operator's declaration — to be assessed by the authority",
    provisionalAtypicalIgnoredTitle: "The ARC-a declaration was not taken into account",
    provisionalSimplifiedTitle: "Preliminary estimate from a simplified calculation",
    dataSourceRlp: "ŘLP UAS geographical zones",
    dataSourceRlpTooltip: "Status from the ŘLP UAS geographical zones over the whole operational volume of the mission (flight area plus the 1:1 contingency buffer by the maximum altitude), SORA 2.5 Figure 6.",
    fastTrackStaleTitle: "Previously selected fast-track is stale",
    fastTrackStaleArc: "The air risk class (ARC) cannot be determined now — a new fast-track selection would be rejected.",
    // Lines explaining the ARC ("Airspace" card reasons, "Why this SAIL level?")
    aec: {
      1: "airport/heliport environment in class B, C or D airspace",
      2: "above 500 ft AGL in a Mode-C veil or TMZ",
      3: "above 500 ft AGL in controlled airspace",
      4: "above 500 ft AGL in uncontrolled airspace over an urban area",
      5: "above 500 ft AGL in uncontrolled airspace over a rural area",
      6: "airport/heliport environment in class E, F or G airspace",
      7: "below 500 ft AGL in a Mode-C veil or TMZ",
      8: "below 500 ft AGL in controlled airspace",
      9: "below 500 ft AGL in uncontrolled airspace over an urban area",
      10: "below 500 ft AGL in uncontrolled airspace over a rural area",
      11: "above FL600",
      12: "atypical airspace — ARC-a by the operator's declaration, to be assessed by the authority",
    },
    lineAec: "AEC {aec}: {description} → initial {initial}",
    lineAecFinal: "AEC {aec}: {description} → initial {initial}, final {final}",
    lineWorstCase: "Worst case — the airspace could not be determined → {arc}",
    lineVolume: "Operational volume: flight geography + {contingency} m contingency (1:1), top {top} m AGL",
    lineVolumeAltitudeMissing: " (maximum altitude missing — worst case)",
    lineZone: "Zone {ident} ({layer}, {role}): {limit}",
    zoneRoleCtr: "CTR/MCTR class D",
    zoneRoleAirport: "airport/heliport environment",
    lineMoreZones: "… and {count} more zones",
    lineData: "ŘLP zone data for {date}: release {release}; boundary {boundary}; data {fingerprint}",
    boundaryNone: "none",
    showAllLines: "Show all ({count})",
    showFewerLines: "Show fewer",
    lineDataLater: "No zone data effective on the mission day; the earliest data, effective later, were used ({layers})",
    recResolve: "Resolve the reasons above — until the ARC is determined the mission can be neither approved nor fast-tracked.",
    recVerifyAuthorization: "Verify authorization requirements before proceeding.",
    recReviewRestrictions: "Review local restrictions and consider risk mitigations.",
    recStandardChecks: "Standard pre-flight checks and procedures apply.",
    // Environment on the mission form
    environmentManualHelp: "Remains the controlling SORA input. A detailed manual population-density assessment (CENSUS/LUISA) can be added later on the mission detail page. Declare a controlled ground area on the mission detail page (Operational Space Declarations), with a reason.",
    environmentAutoTitle: "Determined automatically from the ŘLP UAS zones",
    environmentAutoHelp: "When the mission is saved: the operational volume reaches into the densely populated area (ŘLP LKR316) → urban, otherwise rural. Declare a controlled ground area on the mission detail page (Operational Space Declarations).",
    environmentAutoStored: "Stored: {value}",
    environmentAutoFallbackLabel: "Used only when the airspace cannot be determined (e.g. an area outside the Czech Republic)",
    environmentSettingsUnknown: "The server settings are loading or could not be loaded, so it is not known whether the environment is set automatically from the ŘLP zones; if they do not load, reload the page.",
  },

  // Atypical airspace (ARC-a) declaration on the mission detail page
  atypicalDeclaration: {
    title: "Atypical airspace declaration (ARC-a) — to be assessed by the authority",
    subtitle: "The operator declares atypical airspace for one of the reasons a)–e). The server verifies reasons a), c) and d) over the ŘLP UAS zones; the authority assesses the declaration.",
    notDeclared: "Not declared",
    reasonLabel: "Reason",
    reasons: {
      tsa_tra: "a) flight in a reserved TSA/TRA",
      notam: "b) airspace reserved by a NOTAM",
      prohibited_area_permit: "c) flight in a prohibited area with a permit",
      very_low_altitude: "d) flight up to 30 m AGL outside an airport environment",
      obstacle_proximity: "e) flight near an artificial obstacle (at most 15 m above and 30 m from it)",
    },
    reasonHints: {
      tsa_tra: "Refer to the AUP/UUP reservation in the note.",
      notam: "Give the NOTAM number in the note.",
      prohibited_area_permit: "Give the permit number in the note.",
      very_low_altitude: "Up to 30 m AGL, outside an airport environment. The note is optional.",
      obstacle_proximity: "Obstacle ≤ 15 m above, ≤ 30 m away — describe the obstacle in the note (height of the flight above it and distance from it).",
    },
    noteLabel: "Note",
    notePlaceholder: "Reference, number or description for the chosen reason…",
    noteRequired: "A note is required for the chosen reason.",
    noteTooLong: "The note can have at most {max} characters.",
    reasonRequired: "Choose the reason of the declaration.",
    declare: "Declare",
    edit: "Edit",
    save: "Save",
    saving: "Saving...",
    cancel: "Cancel",
    remove: "Remove declaration",
    removeConfirm: "Remove the atypical airspace (ARC-a) declaration?",
    saved: "ARC-a declaration saved",
    removed: "ARC-a declaration removed",
    saveError: "Failed to save the ARC-a declaration",
    removeError: "Failed to remove the ARC-a declaration",
    conflict: "Another user changed the mission in the meantime — the data were reloaded, please try again.",
    declaredAt: "Declared",
    declaredBy: "Declared by",
    declaredByYou: "you",
    declaredByOther: "another user ({id}…)",
    evaluation: "Evaluation",
  },

  // Wave 2.5: Operator Declarations
  operatorDeclarations: {
    title: "Operational Space Declarations",
    subtitle: "Operator can declare specific conditions affecting GRC",
    gatheringLabel: "Gathering of people",
    gatheringDescription: "Operation takes place in area with gathering of people (concert, sports event, demonstration, etc.)",
    gatheringWarning: "Increases risk to the highest possible level (GRC-7, GRC-8, or out of SORA scope, depending on drone dimension/speed)",
    controlledLabel: "Controlled ground area",
    controlledDescription: "Operator controls access to flight area (construction site, closed facility, military area, etc.)",
    controlledBenefit: "Activation allows GRC-1-2 (lowest risk)",
    reasonLabel: "Declaration reason",
    reasonPlaceholder: "Describe specific circumstances...",
    edit: "Edit",
    save: "Save",
    cancel: "Cancel",
    saving: "Saving...",
    saved: "Declaration saved",
    error: "Error saving",
    noDeclarations: "No declarations are active",
    declaredAt: "Declared",
    grcReduced: "GRC reduced",
  },
  
  // CheckMate
  soraAssistant: {
    title: "CheckMate",
    subtitle: "Your mission authorization guide",
    tabSubtitle: "AI authorization guide",
    // CheckMate tab CTA
    ctaBannerSailHigh: "This mission requires CAA Specific authorization",
    ctaBannerSailHighAction: "CheckMate will help you prepare documentation",
    ctaBannerSailLow: "We recommend reviewing compliance documentation",
    ctaBannerOpenCheckMate: "Open CheckMate",
    ctaLaunch: "Launch CheckMate",
    // Status badges
    statusReady: "Ready",
    statusStepsRemaining: "steps",
    statusActionNeeded: "Action needed",
    // Mode switcher
    modeGenerator: "Generator",
    modeReview: "Review",
    reviewSubtitle: "Let AI review your document",
    // Review mode
    reviewTitle: "Document Review",
    reviewDesc: "Let AI review your ConOps or OM against SORA 2.5 and EU 2019/947",
    uploadDocForReview: "Upload document for review",
    selectFile: "Select file",
    docType: "Type:",
    startReview: "Start review",
    reviewingDoc: "Analyzing document...",
    reviewComplete: "Review completed",
    evaluatedAgainst: "Evaluated against:",
    compliantItems: "Compliant",
    missingItems: "Missing or incorrect",
    recommendationsTitle: "Recommendations",
    needHelpWithFixes: "Need help with fixes?",
    teamCanHelp: "Our team can help you prepare the documentation.",
    downloadReport: "Download review report",
    uploadNewDoc: "Upload revised document",
    // Combined document support
    uploadCombined: "Upload combined ConOps+OM",
    detectedType: "Detected type:",
    combinedDoc: "ConOps + OM (combined)",
    overallScore: "Overall",
    conopsSection: "ConOps section",
    omSection: "OM section",
    useDetected: "Use detected",
    autoDetect: "Auto-detect",
    // Mission context toggle
    missionContextAvailable: "Mission is available as context",
    useMissionSoraContext: "Use mission SORA data as context",
    reviewWithoutMission: "Document will be reviewed against knowledge base only",
    // Generator steps
    step1Title: "Diagnosis",
    step2Title: "Optimizations",
    step3Title: "Action Plan",
    currentAssessment: "Current Mission Assessment",
    specificAuth: "Specific (Standard Authorization)",
    openCategory: "Open Category",
    certifiedCategory: "Certified Category",
    environmentWarning: "Environment Warning",
    whySail: "Why this SAIL level?",
    showOptimizations: "Show Optimizations",
    howToReduce: "How to reduce SAIL?",
    quickWins: "Quick Wins",
    recommended: "Recommended",
    otherOptions: "Other Options",
    selectOptimization: "Select",
    deselectOptimization: "Deselect",
    selectedOptimizations: "Selected optimizations",
    newSailAfter: "New SAIL after applying",
    back: "Back",
    showActionPlan: "Show Action Plan",
    actionPlanTitle: "Action Plan to achieve",
    goal: "Goal",
    step: "Step",
    category: "Category",
    estimatedTime: "Estimated time",
    needHelp: "Need help?",
    teamHelp: "Our team can handle the authorization for you",
    saveAsPdf: "Save as PDF",
    noImprovements: "No improvements possible",
    noImprovementsDesc: "Current configuration is already optimized for this airspace.",
    effort: "Effort",
    effortLow: "Low",
    effortMedium: "Medium",
    effortHigh: "High",
    time: "Time",
    immediate: "Immediate",
    shortTerm: "1-2 days",
    longTerm: "1-2 weeks",
    sailImprovement: "level",
    sailImprovements: "levels",
    authNeeded: "Authorization required",
    equipmentCategory: "Equipment",
    flightParamsCategory: "Flight Parameters",
    authCategory: "Authorization",
    docCategory: "Documentation",
    airspaceFactors: "Airspace factors",
    currentEquipment: "Current equipment",
    parachute: "Parachute",
    fts: "FTS",
    geoAwareness: "Geo-awareness",
    remoteId: "Remote ID",
    noOptSelected: "No optimizations selected",
    selectAtLeast: "Select at least one optimization to see the action plan",
    // Optimization titles
    reduceAltitudeTo: "Reduce maximum altitude to",
    lowerAltitudeReduces: "Lower altitude reduces ground risk",
    addParachute: "Add parachute recovery system",
    parachuteReduces: "Parachute reduces impact energy",
    addFts: "Add Flight Termination System (FTS)",
    ftsEnables: "FTS enables controlled flight termination",
    addGeoAwareness: "Add geo-awareness system",
    geoAwarenessProvides: "Geo-awareness provides airspace boundary protection",
    geoAwarenessNoReduction: "Geo-awareness provides boundary protection but won't reduce ARC inside CTR/restricted airspace",
    useDrone: "Use",
    lighterDrone: "Lighter drone with different equipment",
    changeToVlos: "Change to VLOS operation",
    vlosLowerRisk: "Visual line of sight operation has lower risk",
    envMismatch: "Environment mismatch detected",
    // Airspace reasons
    insideCtr: "Inside CTR",
    nearInternationalAirport: "Near international airport",
    nearRegionalAirport: "Near regional airport",
    highRiskAirspace: "High-risk airspace zone detected",
    // Action plan steps
    installParachute: "Install parachute recovery system",
    contactTechnician: "Contact certified technician for installation and testing before mission",
    installFts: "Install Flight Termination System",
    ftsCertified: "FTS must be certified and tested. May require firmware update.",
    enableGeoAwareness: "Enable geo-awareness feature",
    checkDroneSettings: "Check drone settings or install geo-awareness module",
    reduceAltitude: "Reduce maximum altitude",
    updateMissionParams: "Update mission parameters and ensure this altitude meets operational requirements",
    switchToDrone: "Switch to",
    useAlternativeDrone: "Use alternative drone from fleet",
    changeOpType: "Change operation type to VLOS",
    redesignMission: "Redesign mission to maintain visual line of sight throughout",
    obtainAuthorization: "Obtain airspace authorization",
    ctrRequiresCoordination: "CTR/restricted airspace operation requires ATC coordination. Prepare ConOps documentation.",
    combinedSail: "Combined SAIL",
    withSelectedOptimizations: "with selected optimizations",
    // Apply Changes feature
    applyChanges: "Apply Changes",
    confirmApplyTitle: "Confirm Changes",
    confirmApplyDesc: "The following mission values will be changed:",
    maxAltitudeLabel: "Max. flight altitude",
    aircraftLabel: "Aircraft",
    overwriteWarning: "These changes will overwrite the current mission values.",
    confirmAndSave: "Confirm and Save",
    applying: "Applying...",
    changesApplied: "Changes applied successfully",
    applyError: "Failed to apply changes",
    updatedBySora: "Updated by CheckMate",
    changeDismissed: "Change indicator dismissed",
    dismissError: "Failed to dismiss change",
    // Undo feature
    undoTitle: "Revert Change",
    revert: "Revert",
    changeReverted: "Change reverted successfully",
    undoError: "Failed to revert change",
    // Step 4 - Compliance
    step4Title: "Compliance",
    specificAuthRequired: "This operation requires Specific authorization",
    prepareComplianceDocs: "Prepare compliance documents for Civil Aviation Authority",
    continueToCompliance: "Continue to compliance documents",
    complianceDocs: "Compliance",
    compliance: {
      stepTitle: "Step 4: Compliance Documents",
      stepSubtitle: "Prepare documentation for Civil Aviation Authority",
      readiness: "Readiness",
      soraComplete: "SORA 2.5 assessment complete",
      missionComplete: "Mission data complete",
      omUploaded: "OM template – uploaded",
      omNotUploaded: "OM template – not uploaded",
      conopsUploaded: "ConOps template – uploaded",
      conopsNotUploaded: "ConOps template – not uploaded",
      yourTemplates: "Your Templates",
      uploadOm: "Upload OM template",
      uploadConops: "Upload ConOps template",
      supportedFormats: "Supported formats: .pdf, .docx",
      generateDocuments: "Generate Documents",
      generating: "Generating...",
      generatingConops: "AI agent analyzing mission data and knowledge base...",
      generatingOm: "Filling Operations Manual template...",
      generatingUcl: "Generating complete UCL package...",
      generateConops: "Generate ConOps",
      fillOm: "Fill Operations Manual",
      generateUcl: "Generate UCL Package",
      // Progress steps
      progressStep1: "Analyzing mission data...",
      progressStep2: "Consulting knowledge base...",
      progressStep3: "Generating document sections...",
      progressStep4: "Checking SORA 2.5 compliance...",
      progressStep5: "Finalizing document...",
      conopsGenerated: "ConOps generated",
      omGenerated: "Operations Manual filled",
      uclGenerated: "UCL Package generated",
      usedRegulatoryDocs: "Used regulatory documents:",
      missingFields: "Missing fields",
      more: "more",
      preview: "Preview document",
      downloadWord: "Download as Word",
      downloadPdf: "Download as PDF",
      regenerate: "Regenerate",
      regulatoryBase: "Regulatory base:",
      documentPreview: "Document Preview",
      // Step 4 UX improvements
      defaultTemplateNote: "No custom template uploaded – default template will be used",
      skipCompliance: "I don't need compliance documents for this mission → Skip",
      backToActionPlan: "Back to action plan",
      backToMissionDetail: "Back to mission detail",
    },
  },
  
  // Knowledge Base Admin
  knowledgeBaseAdmin: {
    docTypes: {
      eu_regulation: "EU Regulation",
      czech_regulation: "Czech Regulation",
      sora_methodology: "SORA Methodology",
      reference_document: "Reference Document",
      reference_document_desc: "Reference Document (ConOps, OM, operational documents)",
    },
    stats: {
      referenceDocuments: "Reference Documents",
    },
    toasts: {
      fetchError: "Failed to load documents",
      unsupportedFormat: "Unsupported format. Use .pdf, .docx, .txt or .md",
      requiredFields: "Fill in all required fields",
      uploaded: "Document uploaded: {message}",
      uploadError: "Upload failed",
      deactivated: "Document deactivated",
      activated: "Document activated",
      toggleError: "Operation failed",
      deleted: "Document deleted",
      deleteError: "Delete failed",
    },
  },

  // Insurance Management
  insurance: {
    title: "Insurance Management",
    subtitle: "Fleet insurance policies",
    addPolicy: "Add Policy",
    editPolicy: "Edit Policy",
    deletePolicy: "Delete Policy",
    noPolicies: "No insurance policies",
    noPoliciesDesc: "Add the first insurance policy for your fleet",
    // Table columns
    policyNumber: "Policy Number",
    insurer: "Insurer",
    coverageType: "Coverage Type",
    coverageAmount: "Coverage Amount",
    validFrom: "Valid From",
    validUntil: "Valid Until",
    coveredAircraft: "Covered Aircraft",
    allAircraft: "All Aircraft",
    status: "Status",
    actions: "Actions",
    document: "Document",
    notes: "Notes",
    // Coverage types
    liability: "Liability",
    hull: "Hull",
    both: "Liability + Hull",
    // Status badges
    valid: "Valid",
    expiringSoon: "Expiring Soon",
    expired: "Expired",
    daysRemaining: "days remaining",
    daysAgo: "days overdue",
    // Stats
    totalPolicies: "Total Policies",
    validPolicies: "Valid Policies",
    expiringPolicies: "Expiring Soon",
    expiredPolicies: "Expired",
    highestCoverage: "Highest Coverage",
    earliestExpiry: "Earliest Expiry",
    // Form
    policyDetails: "Policy Details",
    selectCoverageType: "Select coverage type",
    amountInEur: "Amount in EUR",
    selectAircraft: "Select aircraft (empty = all)",
    uploadPdf: "Upload Policy PDF",
    dropPdfHere: "Drop PDF here or click to browse",
    pdfUploaded: "PDF uploaded",
    replacePdf: "Replace PDF",
    viewPdf: "View PDF",
    // Warnings
    coverageWarning: "Coverage below EU 2019/947 minimum",
    coverageMinimum: "Minimum required coverage: €750,000",
    noValidInsurance: "No valid insurance",
    insuranceExpiresSoon: "Insurance expires in {days} days",
    insuranceExpired: "Insurance has expired",
    // Confirmation
    deleteConfirm: "Are you sure you want to delete this policy?",
    deleteWarning: "This action cannot be undone.",
    // Success messages
    policyCreated: "Policy created",
    policyUpdated: "Policy updated",
    policyDeleted: "Policy deleted",
    documentUploaded: "Document uploaded",
    // Filter
    filterByStatus: "Filter by status",
    allStatuses: "All statuses",
  },

  // Operational Authorizations
  authorizations: {
    title: "Operational Authorizations",
    subtitle: "Authorizations for Specific Category operations",
    addAuthorization: "Add Authorization",
    editAuthorization: "Edit Authorization",
    deleteAuthorization: "Delete Authorization",
    revokeAuthorization: "Revoke Authorization",
    noAuthorizations: "No operational authorizations",
    noAuthorizationsDesc: "Add the first authorization for your organization",
    // Table columns
    referenceNumber: "Reference Number",
    authorizationType: "Type",
    authTitle: "Title",
    issuingAuthority: "Issuing Authority",
    issuedAt: "Issued At",
    validFrom: "Valid From",
    validUntil: "Valid Until",
    conditions: "Conditions",
    sailLevels: "SAIL Levels",
    coveredAircraft: "Covered Aircraft",
    allAircraft: "All Aircraft",
    status: "Status",
    document: "Document",
    notes: "Notes",
    // Authorization types (new enum values)
    types: {
      STS_01: "STS-01 (VLOS over controlled area)",
      STS_02: "STS-02 (BVLOS over sparsely populated)",
      PDRA_S01: "PDRA-S01",
      PDRA_S02: "PDRA-S02",
      PDRA_G01: "PDRA-G01",
      LUC: "LUC (Light UAS Operator Certificate)",
      OPERATIONAL_AUTHORIZATION: "Operational Authorization",
      CUSTOM: "Custom"
    },
    // Status badges (all enum values including application stages)
    statuses: {
      draft: "Draft",
      submitted: "Submitted",
      under_review: "Under Review",
      needs_amendment: "Needs Amendment",
      active: "Active",
      expired: "Expired",
      suspended: "Suspended",
      revoked: "Revoked"
    },
    // Legacy status badges (for backward compatibility)
    status_active: "Active",
    status_expired: "Expired",
    status_suspended: "Suspended",
    status_revoked: "Revoked",
    status_draft: "Draft",
    status_submitted: "Submitted",
    status_under_review: "Under Review",
    status_needs_amendment: "Needs Amendment",
    daysRemaining: "days remaining",
    daysAgo: "days overdue",
    // Stats
    totalAuthorizations: "Total Authorizations",
    activeAuthorizations: "Active",
    expiringAuthorizations: "Expiring Soon",
    expiredAuthorizations: "Expired",
    suspendedAuthorizations: "Suspended",
    revokedAuthorizations: "Revoked",
    coveredSailLevels: "Covered SAIL Levels",
    // Form
    authorizationDetails: "Authorization Details",
    selectType: "Select authorization type",
    conditionsHint: "Enter a condition and press Enter",
    addCondition: "Add condition",
    sailLevelsHint: "Select SAIL levels this authorization covers",
    uploadPdf: "Upload Authorization PDF",
    dropPdfHere: "Drop PDF here or click to browse",
    pdfUploaded: "PDF uploaded",
    replacePdf: "Replace PDF",
    viewPdf: "View PDF",
    // Scope fields
    scope: {
      title: "Scope Details",
      maxHeight: "Max Height AGL (m)",
      maxDistance: "Max Distance (m)",
      airspaceClasses: "Airspace Classes",
      geographicLimitations: "Geographic Limitations",
      operationalVolume: "Operational Volume",
      groundRiskClass: "Ground Risk Class (GRC)",
      airRiskClass: "Air Risk Class (ARC)",
      containmentRequirements: "Containment Requirements",
      privileges: "Privileges",
      maxSailLevel: "Max SAIL Level",
      droneClasses: "Drone Classes",
      omaReference: "OMA Reference",
      description: "Description",
      limitations: "Limitations",
      comingSoon: "Scope details coming soon for this type"
    },
    // Warnings
    noValidAuthorization: "No valid authorization for SAIL {level}",
    authorizationExpiresSoon: "Authorization expires in {days} days",
    authorizationExpired: "Authorization has expired",
    authorizationRequired: "SAIL {level} mission requires operational authorization",
    sailNotRequired: "SAIL I-II does not require operational authorization",
    // Confirmation
    deleteConfirm: "Are you sure you want to delete this authorization?",
    deleteWarning: "This action cannot be undone.",
    revokeConfirm: "Are you sure you want to revoke this authorization?",
    revokeWarning: "Revoked authorizations cannot be used for mission approval.",
    // Success messages
    authorizationCreated: "Authorization created",
    authorizationUpdated: "Authorization updated",
    authorizationDeleted: "Authorization deleted",
    authorizationRevoked: "Authorization revoked",
    authorizationSuspended: "Authorization suspended",
    documentUploaded: "Document uploaded",
    loadError: "Failed to load authorization",
    saveError: "Failed to save authorization",
    scopeChangeConfirm: "Unsaved changes to this authorization will be lost. Switch organization anyway?",
    // Filter
    filterByStatus: "Filter by status",
    filterByType: "Filter by type",
    filterBySail: "Filter by SAIL",
    allStatuses: "All statuses",
    allTypes: "All types",
    allSailLevels: "All levels",
    // Widget
    expiringWidget: {
      title: "Authorizations Expiring Soon",
      viewAll: "View All",
      noExpiring: "No authorizations expiring soon",
      daysLeft: "days left"
    }
  },
  
  // CheckMate - Fast Track & LUC (Sprint E4)
  checkmate: {
    fastTrack: {
      sectionTitle: "Fast Track Recommendations",
      sectionSubtitle: "Faster paths to compliance using your authorizations",
      appliesBadge: "Applies",
      verifyBadge: "Verify Scope",
      expiredBadge: "Expired",
      expiringSoonBadge: "Expires in {days} days",
      timeSaved: "Save {days} days",
      timeSavedShort: "{days}d saved",
      useThisPath: "Use this path",
      verifyAndAuthorize: "Verify and authorize",
      referenceNumber: "Ref: {ref}",
      validUntil: "Valid until {date}",
      alreadyApplied: "Fast track already applied",
      appliedBy: "Applied by {email} on {date}",
      noFastTracks: "No fast track options available for this mission",
      
      subtypes: {
        STS_DECLARATION: "STS Declaration",
        PDRA_REGISTRATION: "PDRA Registration",
        LUC_SELF_AUTHORIZE: "LUC Self-Authorize",
        OPERATIONAL_AUTH_USE: "Operational Authorization",
      },
      
      successToast: "Fast track applied successfully",
      errorToast: "Failed to apply fast track",
    },
    
    standardMitigations: {
      sectionTitle: "Standard Mitigations",
      sectionSubtitle: "Alternative paths via M1/M2 mitigations",
    },
    
    lucVerification: {
      title: "LUC Scope Verification",
      subtitle: "Verify your LUC covers this mission",
      provides: "Your LUC provides:",
      requires: "This mission requires:",
      appearsToCover: "Your LUC appears to cover this mission.",
      confirmCheckbox: "I confirm I have verified the LUC scope matches this mission and accept compliance responsibility.",
      selfAuthorizeBtn: "Self-Authorize Mission",
      cancelBtn: "Cancel",
      
      fields: {
        maxSailLevel: "Max SAIL Level",
        droneClasses: "Covered Drone Classes",
        privileges: "Privileges",
        missionSail: "Mission SAIL",
        missionDroneClass: "Mission Drone Class",
      },
    },
    
    expiringSoon: {
      bannerTitle: "Authorizations expiring soon",
      item: "{type} ({ref}) — expires in {days} days",
      renewLink: "Renew",
    },
    
    expired: {
      bannerTitle: "Expired authorizations",
      item: "{type} ({ref}) — expired {date}",
    },
    
    // WAVE 2A: Flight Mode translations
    flightMode: {
      sectionTitle: "Flight Mode",
      sectionSubtitle: "Select operation type",
      vlos: "VLOS",
      vlosDescription: "Visual Line of Sight — direct visual contact with drone",
      evlos: "EVLOS",
      evlosDescription: "Extended VLOS — with observer chain",
      bvlos: "BVLOS",
      bvlosDescription: "Beyond VLOS — beyond direct line of sight",
      impactNote: "Flight mode affects operational category",
      openEligible: "Open category eligible",
      specificRequired: "Specific category required",
    },
    
    // WAVE 2A: Containment translations
    containment: {
      sectionTitle: "Containment Configuration",
      sectionSubtitle: "Safety system settings for this mission",
      geoFence: "Geo-fence",
      geoFenceEnabled: "Enabled",
      geoFenceDisabled: "Disabled",
      geoFenceNotApplicable: "Not applicable",
      returnToHome: "Return-to-Home",
      rthAutomatic: "Automatic",
      rthManual: "Manual",
      rthNone: "None",
      fts: "Flight Termination System (FTS)",
      ftsParachute: "Parachute",
      ftsKinetic: "Kinetic termination",
      ftsNone: "None",
      observer: "Dedicated observer",
      kineticEnergyLimit: "Kinetic energy limit (J)",
      containmentLevel: "Containment level",
      levelRobust: "Robust",
      levelStandard: "Standard",
      levelMinimal: "Minimal",
      sailImpact: "SAIL impact",
      fromAircraftProfile: "From aircraft profile",
      operatorOverride: "Operator override",
      aircraftLacksEquipment: "Aircraft does not have this equipment",
    },
  },
  
  // Organizations page
  organizations: {
    title: "Organizations",
    subtitle: "Manage drone operator companies",
    searchPlaceholder: "Search organizations...",
    addButton: "Add Organization",
    columns: {
      companyName: "Company Name",
      type: "Type",
      category: "Category",
      subscription: "Subscription",
      authorization: "Authorization",
      responsiblePerson: "Responsible Person",
      compliance: "Compliance",
    },
    badges: {
      specific: "Specific",
      industrial: "Industrial",
      enterprise: "Enterprise",
      open: "Open",
      free: "Free",
      professional: "Professional",
    },
    emptyState: "No organizations found",
    errorLoading: "Failed to load organizations",
    updated: "Organization updated successfully",
    created: "Organization created successfully",
    saveError: "Failed to save organization",
    deleted: "Organization deleted",
    deleteError: "Failed to delete organization",
    subscriptionUpdated: "Subscription updated to {tier}",
    subscriptionUpdateError: "Failed to update subscription",
  },
  
  // Users page
  users: {
    title: "Users",
    subtitle: "All platform users",
    orgMembers: "Organization members",
    searchPlaceholder: "Search users...",
    platformLevel: "Platform Level",
    adminOnly: "Only administrators can view users",
    columns: {
      user: "User",
      email: "Email",
      role: "Role",
      organization: "Organization",
      status: "Status",
    },
    emptyState: "No users found",
    errorLoading: "Failed to fetch users",
    statuses: {
      active: "Active",
      inactive: "Inactive",
      pending: "Pending",
    },
    fields: {
      name: "Name",
      newPassword: "New Password",
    },
    actions: {
      createUser: "New User",
      resetPassword: "Reset Password",
      deactivate: "Deactivate",
      reactivate: "Reactivate",
      deleteUser: "Delete",
    },
    confirm: {
      deleteUser: "This will PERMANENTLY and IRREVERSIBLY delete this user account. This cannot be undone. Continue?",
    },
    dialogs: {
      createTitle: "Create User",
      editTitle: "Edit User",
      resetPasswordTitle: "Reset Password",
    },
    validation: {
      nameRequired: "Name is required.",
      emailRequired: "Email is required.",
      emailInvalid: "A valid email address is required.",
      passwordLength: "Password must be between 6 and 20 characters long.",
      organizationRequired: "Organization is required for this role.",
      demotionOrganizationRequired: "Changing a platform administrator's role requires an organization to assign them to.",
    },
    messages: {
      created: "User created",
      updated: "User updated",
      deactivated: "User deactivated",
      reactivated: "User reactivated",
      deleted: "User deleted",
      passwordReset: "Password reset",
    },
    selfActionHint: "You cannot change your own role or status.",
    demotionOrgHint: "A platform administrator has no organization — pick the one this account moves into.",
    promotionOrgNotice: "A platform administrator has no organization — this account will be removed from its current one.",
    createOrgLockedHint: "New users are created in the active organization. Switch organizations in the header to create one elsewhere.",
  },
  
  // Bulletins page
  bulletins: {
    title: "Compliance Bulletins",
    subtitle: "Create and manage regulatory bulletins for operators",
    tabs: {
      all: "All",
      drafts: "Drafts",
      sent: "Sent",
    },
    columns: {
      title: "Title",
      type: "Type",
      severity: "Severity",
      status: "Status",
      effective: "Effective",
      recipients: "Recipients",
      actions: "Actions",
    },
    badges: {
      systemInfo: "System Info",
      regulatoryChange: "Regulatory Change",
      informational: "Informational",
      critical: "Critical",
      important: "Important",
      sent: "Sent",
      draft: "Draft",
      all: "All",
    },
    newBulletin: "New Bulletin",
    attributionBy: "by",
    errorLoading: "Failed to load bulletins",
    selectOrgRequired: "Select at least one organization",
    titleRequired: "Title is required",
    contentRequired: "Content is required",
    updated: "Bulletin updated",
    created: "Bulletin created",
    saveError: "Failed to save bulletin",
    selectKbDocFirst: "Select a KB document first",
    aiGenerated: "AI generated content - review and edit as needed",
    aiGenerateError: "AI generation failed",
    deleted: "Bulletin deleted",
    deleteError: "Failed to delete",
    sent: "Bulletin sent to all recipients",
    sendError: "Failed to send",
  },
  
  // News & Regulations page
  news: {
    title: "News & Regulations",
    subtitle: "Stay informed about regulatory changes and important updates",
    tabs: {
      all: "All",
      regulatory: "Regulatory",
      templates: "Templates",
      system: "System",
    },
    severities: {
      allSeverities: "All Severities",
      informational: "Informational",
      critical: "Critical",
      important: "Important",
      systemInfo: "System Info",
      regulatoryChange: "Regulatory Change",
    },
    labels: {
      effective: "Effective",
      readLink: "Read",
    },
    errorLoading: "Failed to load news",
  },
  
  // Centralized error messages
  errors: {
    failedToLoadPilots: "Failed to load pilots",
    failedToLoadAircraft: "Failed to load aircraft",
    failedToLoadAuthorizations: "Failed to load authorizations",
    failedToLoadInsurance: "Failed to load insurance policies",
    failedToLoadUsers: "Failed to fetch users",
    failedToLoadOrganizations: "Failed to load organizations",
    failedToLoadMissions: "Failed to load missions",
    failedToLoadBulletins: "Failed to load bulletins",
    failedToLoadNews: "Failed to load news",
    failedToCreate: "Failed to create",
    failedToUpdate: "Failed to update",
    failedToDelete: "Failed to delete",
    resetPasswordFailed: "Failed to reset password",
    networkError: "Network error",
    unauthorized: "Unauthorized",
    forbidden: "Access denied",
    serverError: "Server error",
    // Quota errors (Sprint H1.5.2)
    quota: {
      orgQuotaExceeded: "Organization exceeded hourly request limit for your tier. Try again later or upgrade.",
      concurrentAiLimit: "Concurrent AI request limit reached. Try again shortly.",
      rateLimitExceeded: "Rate limit exceeded. Try again later.",
      upgradeRequired: "This feature requires a higher tier.",
      limitReached: "Limit reached",
      tryAgainLater: "Try again later",
      upgradeNow: "Upgrade now",
    },
    // AI Quota errors (Sprint H1.5.3)
    aiQuota: {
      exhausted: "Monthly AI call limit exhausted. Upgrade your plan or buy a topup.",
      approachingLimit: "You are approaching your monthly AI call limit.",
      usagePercent: "Used {percent}% of monthly AI limit.",
      callsRemaining: "{remaining} AI calls remaining this month.",
      resetAt: "Limit resets on {date}.",
      upgradeOrTopup: "Upgrade your plan or buy a topup.",
    },
    // Resource Quota errors (Sprint H1.5.4)
    resourceQuota: {
      exceeded: "Resource limit for your tier reached.",
      missions: "Monthly mission limit for your tier reached.",
      pilots: "Pilot limit for your tier reached.",
      documents: "Document limit for your tier reached.",
      storage: "Storage limit for your tier reached.",
      fileTooLarge: "File is too large. Maximum size is 50 MB.",
      upgradeRequired: "Upgrade your plan to create more items.",
      current: "Current: {current}",
      limit: "Limit: {limit}",
    },
    // Server errors (Sprint H1.5.4)
    server: {
      requestTimeout: "Request took too long.",
      gatewayTimeout: "Server did not respond in time.",
    },
  },
  
  // Admin Usage Dashboard (Sprint H1.5.5)
  adminUsage: {
    title: "Platform Usage Dashboard",
    subtitle: "Monitor organization usage and manage resource limits",
    totalOrgs: "Total Organizations",
    highUsage: "High Usage (>80%)",
    anomalies: "Anomalies",
    unlimitedTier: "Unlimited Tier",
    organization: "Organization",
    tier: "Tier",
    missions: "Missions",
    pilots: "Pilots",
    documents: "Documents",
    storage: "Storage",
    apiCalls: "API Calls",
    aiCalls: "AI Calls",
    lastActivity: "Last Activity",
    actions: "Actions",
    allTiers: "All Tiers",
    searchOrgs: "Search organizations...",
    sortHighestUsage: "Highest Usage",
    sortApiCalls: "API Calls",
    sortAiCalls: "AI Calls",
    sortStorage: "Storage",
    noOrgsFound: "No organizations found",
    resourceUsage: "Resource Usage",
    apiUsage: "API Usage",
    used: "Used",
    limit: "Limit",
    createOverride: "Create Override",
    resource: "Resource",
    newLimit: "New Limit",
    reason: "Reason",
    reasonPlaceholder: "Customer requested temporary increase for project X",
    expiresAt: "Expires At",
    saveOverride: "Save Override",
    overrideCreated: "Override created successfully",
    overrideValidation: "Please fill all fields. Reason must be at least 10 characters.",
    anomalyAlerts: "Anomaly Alerts",
    accessDenied: "Access Denied",
    platformAdminOnly: "This page is only accessible to platform administrators.",
  },

  // Admin Feature Flags (Sprint H2.4)
  adminFeatures: {
    title: "Feature Flags",
    subtitle: "Manage per-organization features",
    organization: "Organization",
    tier: "Tier",
    defaultFeatures: "Default Features",
    explicitlyEnabled: "Explicitly Enabled",
    effectiveFeatures: "Effective Features",
    availableFeatures: "Available Features",
    auditLog: "Audit Log",
    enable: "Enable",
    disable: "Disable",
    enabled: "Enabled",
    disabled: "Disabled",
    defaultForTier: "Default for this tier",
    cannotDisableDefault: "Cannot disable default features",
    enableFeature: "Enable Feature",
    disableFeature: "Disable Feature",
    reason: "Reason",
    reasonPlaceholder: "Explain why this change is being made (min 10 characters)...",
    reasonTooShort: "Reason must be at least 10 characters",
    confirmEnable: "Confirm Enable",
    confirmDisable: "Confirm Disable",
    featureEnabled: "Feature enabled successfully",
    featureDisabled: "Feature disabled successfully",
    noAuditEntries: "No audit entries yet",
    auditAction: "Action",
    auditAdmin: "Admin",
    auditReason: "Reason",
    auditTimestamp: "Time",
    loading: "Loading...",
    error: "Error loading features",
    accessDenied: "Access Denied",
    platformAdminOnly: "Only platform administrators can manage feature flags.",
    backToOrgs: "Back to Organizations",
  },

  // Geometry Import - Phase X1
  // Mission take-off point + flight area (mission-map unification, PR6)
  flightArea: {
    sectionTitle: "Take-off point and flight area",
    sectionHelper: "Place the take-off point first, then draw the flight area (a polygon, a route with a corridor or a circle).",
    takeoffPoint: "Take-off point",
    setTakeoff: "Place take-off point",
    setTakeoffActive: "Click the map…",
    takeoffHelper: "Click the map, drag the marker or search for an address",
    takeoffRequired: "Set the take-off point",
    latitude: "Latitude",
    longitude: "Longitude",
    searchPlaceholder: "Search address or place…",
    search: "Find",
    searchNotFound: "Location not found",
    searchFailed: "Search failed",
    area: "Flight area",
    drawPolygon: "Draw area (polygon)",
    drawLine: "Draw route (line with corridor)",
    drawHint: "Draw the area with the tools in the top left corner of the map.",
    replaceConfirm: "Replace the existing flight area?",
    editVertices: "Edit vertices",
    deleteArea: "Delete area",
    deleteConfirm: "Delete the flight area? The mission cannot be saved without one.",
    areaRequired: "Draw the flight area",
    typePolygon: "Polygon (area)",
    typeLine: "Line (route with corridor)",
    typeCircle: "Circle",
    geometryType: "Area type",
    vertexCount: "Vertices",
    length: "Route length",
    perimeter: "Perimeter",
    areaSize: "Area",
    corridorArea: "Corridor area",
    corridorWidth: "Corridor width (m)",
    corridorWidthHelper: "Total width, half on each side of the centreline ({min}–{max} m)",
    corridorApply: "Apply",
    corridorWidthAction: "Corridor width",
    corridorWidthActionTitle: "Set the route's corridor width",
    corridorWidthRequired: "Enter the corridor width",
    corridorWidthRange: "Corridor width must be {min}–{max} m",
    circleRadius: "Circle radius (m)",
    circleRadiusHelper: "Radius from the circle centre ({min}–{max} m)",
    circleRadiusAction: "Radius",
    circleRadiusActionTitle: "Set the circle radius",
    circleRadiusRequired: "Enter the circle radius",
    circleRadiusRange: "The radius must be {min}–{max} m.",
    circleRadiusFixBeforeSave: "Fix the radius or cancel the edit.",
    circleCenter: "Circle centre",
    takeoffAtCircleCenter: "Take-off at the circle centre",
    coordinates: "Vertex coordinates",
    showCoordinates: "Show coordinates",
    hideCoordinates: "Hide coordinates",
    vertex: "Vertex",
    takeoffInside: "The take-off point lies within the flight area",
    takeoffNear: "The take-off point is {distance} from the flight area",
    takeoffWarning: "The take-off point is {distance} from the flight area (more than {limit}) — check its position",
    takeoffTooFar: "The take-off point is too far from the flight area ({distance}, max {max})",
    validating: "Checking the area…",
    validationUnavailable: "The area could not be checked — saving will decide whether it is valid.",
    importFromFile: "Import from file",
    importAfterSave: "File import becomes available once the mission is saved",
    importSingleOnly: "The file contains {count} features. Only a file with a single polygon or line can be used for now.",
    importHoles: "Polygons with holes are not supported",
    importPoints: "The file contains only points — a flight area cannot be created from them yet",
    importUnsupported: "This geometry type cannot be used as a flight area (polygon or line only)",
    importEmpty: "The file contains no geometry",
    importApplied: "Geometry applied to the form — it is saved with the mission",
    useGeometry: "Use geometry",
    legacyNoArea: "This mission has no flight area. Add it in the mission editor — without it the mission cannot be saved or approved.",
    legacyInvalidArea: "The stored flight area is not valid ({reason}). Please draw it again.",
    legacyNoTakeoff: "This mission has no take-off point. Add it in the mission editor.",
    legacyNoWidth: "The route has no corridor width. Add it in the mission editor.",
    legacyNoRadius: "The circle has no radius. Add it in the mission editor.",
    reasonHoles: "polygon with holes",
    reasonPoints: "points only",
    reasonUnsupported: "unsupported geometry type",
    reasonMalformed: "malformed geometry",
    lockedCompleted: "The mission is completed — the area and take-off point cannot be changed.",
    takeoffAddress: "Take-off address",
    editMission: "Edit mission",
    legendTitle: "Map layers",
    legendTakeoff: "Take-off point",
    legendArea: "Flight area",
    legendCircle: "Flight area (circle)",
    legendRoute: "Route",
    legendCorridor: "Corridor",
    airports: "Airports",
    missingForSave: "Missing before saving: {items}",
    invalidForSave: "Cannot save — {items}",
    finishMapSession: "Finish or cancel the flight area change on the map (buttons next to the map tools, top left).",
    takeoffTooFarUnmeasured: "The take-off point is too far from the flight area.",
  },

  uasZones: {
    title: "UAS zones (ŘLP)",
    themes: {
      aviation: "Aviation",
      defence: "National defence",
      nature: "Nature protection",
      infrastructure: "Infrastructure",
      population: "Densely populated area",
    },
    zoomInFor: "Zoom in to see:",
    tooMany: "Too many zones in this view — zoom in or switch some layers off.",
    error: "Zones could not be loaded.",
    noData: "Zone data have not been imported.",
    source: "Source: ŘLP ČR",
    dataAsOf: "data as of",
    disclaimer: "For information only — check binding boundaries in DroneMap.",
    popupHeight: "Zone height",
    popupMaxHeight: "Max. permitted height",
    popupDataAsOf: "ŘLP data as of",
    attribution: "UAS geographic zones © ŘLP ČR",
  },
  geometryImport: {
    title: "Import Geometry",
    subtitle: "Import flight zone from KML, GeoJSON, GPX, or CSV",
    importButton: "Import Geometry",
    dropHere: "Drop file here...",
    dragDrop: "Drag & drop file here, or click to select",
    supportedFormats: "Supported: KML, KMZ, GeoJSON, GPX, CSV",
    formatHint: "Format",
    autoDetect: "Auto-detect",
    gpxPrefer: "GPX Preference",
    waypoints: "Waypoints",
    track: "Track/Route",
    latColumn: "Latitude Column",
    lonColumn: "Longitude Column",
    loadPreview: "Load Preview",
    previewLoaded: "Preview loaded",
    previewFailed: "Failed to load preview",
    features: "Features",
    points: "Points",
    validGeometry: "Valid Geometry",
    invalidGeometry: "Invalid Geometry",
    simplify: "Simplify Geometry",
    reduced: "Reduced",
    suggestions: "Suggestions",
    cancel: "Cancel",
    import: "Import Geometry",
    importSuccess: "Geometry imported successfully",
    commitFailed: "Failed to save geometry",
    fileTooLarge: "File too large",
    unsupportedFormat: "Unsupported file format",
    noFileSelected: "No file selected",
    // SAIL change notifications
    sailChanged: "SAIL Changed",
    sailChangedFrom: "SAIL level changed from",
    sailChangedTo: "to",
    checkmateNeedsReview: "Geometry changed — recommend re-running CheckMate",
    rerunCheckmate: "Re-run CheckMate",
  },

  // Aircraft Calendar - Phase X2
  aircraft: {
    calendar: {
      title: "Flight Schedule",
      subtitle: "Overview of scheduled missions for your drones",
      perDrone: "Per Drone",
      fleet: "Fleet",
      selectDrone: "Select drone:",
      noMissions: "No missions in this range",
      noDrones: "No drones",
      missions: "missions",
      drones: "drones",
      day: "Day",
      week: "Week",
      month: "Month",
      today: "Today",
      previous: "Back",
      next: "Next",
      fleetOverview: "Fleet Overview",
      drone: "Drone",
      status: "Status",
      telemetry: "Telemetry",
      draft: "Draft",
      ready: "Ready",
      active: "In Progress",
      completed: "Completed",
      live: "Live",
      offline: "Offline",
      noData: "No data",
      mission: "Mission",
      pilot: "Pilot",
      time: "Time",
      sail: "SAIL",
      tierLimitError: "Free tier supports max 30-day range. Upgrade for longer ranges.",
      upgradePlan: "Upgrade plan",
      dateRange: "Date range:",
      clickToCreate: "Click to create mission",
      show24h: "Show full 24h",
      show16h: "Show 06-22h",
      timeTooltip: "Time"
    },
    maintenance: {
      title: "Maintenance",
      subtitle: "Schedule and track aircraft maintenance",
      scheduleNew: "Schedule Maintenance",
      noMaintenance: "No maintenance scheduled yet.",
      scheduleFirst: "Schedule first maintenance",
      upcoming: "Upcoming",
      inProgress: "In Progress",
      completed: "Completed",
      cancelled: "Cancelled",
      history: "Maintenance History",
      currentlyInMaintenance: "Aircraft is currently in maintenance",
      // Scheduler modal
      scheduler: {
        createTitle: "Schedule Maintenance",
        editTitle: "Edit Maintenance",
        startAt: "Start",
        endAt: "End",
        type: "Maintenance Type",
        reason: "Reason",
        reasonPlaceholder: "Describe the reason for maintenance (min. 5 chars)",
        technician: "Technician / Service Provider",
        technicianPlaceholder: "Optional",
        notes: "Notes",
        notesPlaceholder: "Additional notes",
        submit: "Save",
        cancel: "Cancel",
        cancelMaintenance: "Cancel Maintenance",
        cancelReason: "Cancellation Reason",
        confirmCancel: "Are you sure you want to cancel this maintenance?",
        forceSchedule: "Schedule Anyway"
      },
      // Types
      types: {
        scheduled: "Scheduled",
        unscheduled: "Unscheduled",
        calibration: "Calibration",
        repair: "Repair",
        inspection: "Inspection"
      },
      // Statuses
      statuses: {
        scheduled: "Scheduled",
        in_progress: "In Progress",
        completed: "Completed",
        cancelled: "Cancelled"
      },
      // Conflicts
      conflicts: {
        title: "Mission Conflicts",
        message: "This maintenance window conflicts with the following missions:",
        forceCreate: "Create Maintenance Anyway",
        cancelCreation: "Cancel",
        missionWarning: "Selected aircraft is in maintenance during this period. Change mission time or select another aircraft."
      },
      // Table headers
      dateRange: "Period",
      typeHeader: "Type",
      statusHeader: "Status",
      reasonHeader: "Reason",
      technicianHeader: "Technician",
      actions: "Actions",
      edit: "Edit",
      view: "View",
      detail: {
        info: "Info",
        serialNumber: "Serial Number",
        registration: "Registration",
        flightHours: "Flight Hours",
        lastMaintenance: "Last Maintenance",
        serviceInterval: "Service Interval"
      }
    }
  },
  mission: {
    maintenance_conflict: {
      warning: "Selected aircraft is in maintenance during this period.",
      change_time: "Change mission time or select another aircraft."
    }
  },
  // Wave 2B: Radio Telephony
  radioTelephony: {
    title: "Radio Telephony Licenses",
    addLicense: "Add RT License",
    editLicense: "Edit RT License",
    licenseType: "License Type",
    licenseNumber: "License Number",
    issuer: "Issuing Authority",
    issuedAt: "Issue Date",
    expiresAt: "Expiry Date",
    languages: "Languages",
    languagesHint: "Select languages for which the RT license is valid",
    notes: "Notes",
    notesPlaceholder: "Optional notes...",
    licenseNumberRequired: "License number is required",
    issueDateRequired: "Issue date is required",
    expiryDateRequired: "Expiry date is required",
    languageRequired: "At least one language is required",
    licenseAdded: "RT license added",
    licenseUpdated: "RT license updated",
    licenseDeleted: "RT license deleted",
    saveFailed: "Failed to save license",
    deleteFailed: "Failed to delete license",
    confirmDelete: "Are you sure you want to delete this RT license?",
    other: "Other",
    // Status badges
    status: {
      valid: "Valid",
      expiringSoon: "Expiring Soon",
      expired: "Expired",
      none: "None"
    },
    // Mission validation
    missionWarning: "Mission in controlled airspace (CTR/ATZ) requires valid Radio Telephony license.",
    missionBlocked: "Pilot does not have valid RT license for controlled airspace operations.",
    noLicenses: "No RT licenses added yet",
    hint: "Required for operations in controlled airspace (CTR/ATZ)"
  },
  // Wave 2B: Remote ID
  remoteId: {
    title: "Remote ID Configuration",
    ridType: "Remote ID Type",
    ridSerial: "RID Module Serial Number",
    ridSerialHint: "Optional: Serial number of the RID module for traceability",
    exemptionId: "Exemption ID",
    exemptionIdHint: "The official exemption certificate ID from national CAA",
    exemptionExpires: "Exemption Expires At",
    exemptionIdRequired: "Exemption ID is required when status is 'Exempted'",
    ridUpdated: "Remote ID configuration updated",
    saveFailed: "Failed to save RID configuration",
    // Types
    builtIn: "Built-in (Factory Integrated)",
    addOn: "Add-on Module",
    modular: "Modular (External)",
    broadcastOnly: "Broadcast Only",
    exempted: "Exempted (With Documentation)",
    none: "None / Not Equipped",
    // Status
    compliantStatus: "RID Compliant",
    nonCompliantStatus: "Non-Compliant",
    exemptedStatus: "Exempted (requires documentation)",
    compliantHint: "This aircraft can be used for EU operations requiring RID.",
    nonCompliantHint: "This aircraft cannot be used for operations requiring RID.",
    exemptedHint: "Provide exemption documentation ID below.",
    euRegInfo: "EU Regulation 2019/947 requires Remote ID for most drone operations in EU airspace as of 2024. Aircraft without compliant RID cannot be assigned to missions in regulated airspace.",
    // Mission validation
    missionWarning: "Mission requires Remote ID compliant aircraft. Selected aircraft is not RID compliant.",
    missionBlocked: "Aircraft is not Remote ID compliant and cannot be used for this mission."
  },
  
  // Wave 2.5 Block D: Map Settings
  mapSettings: {
    title: "Map Settings",
    description: "Third-party map service configuration",
    mapyCzTitle: "Mapy.cz API Key",
    mapyCzDescription: "To use Mapy.cz as a base map, you need an API key.",
    currentKey: "Current Key",
    noKeyConfigured: "No key configured",
    newKey: "New API Key",
    placeholder: "Enter your Mapy.cz API key",
    save: "Save",
    saving: "Saving...",
    testKey: "Test Key",
    testing: "Testing...",
    testSuccess: "Key format is valid",
    testFailed: "Invalid key format",
    saved: "Settings saved",
    error: "Error saving",
    warning: "Mapy.cz free tier: 5000 tiles/day. For heavy usage, we recommend a paid tier.",
    getKeyLink: "Get API Key",
    noPermission: "You do not have permission to edit map settings"
  },
  
  // Wave 2.5 Block D: Map Layers
  mapLayers: {
    title: "Layers",
    baseMaps: "Base Maps",
    overlays: "Overlays",
    missionPolygon: "Mission Polygon",
    airspaces: "Airspaces",
    densityHeatmap: "Population Density",
    settlements: "Settlements",
    landuse: "Land Use",
    requiresApiKey: "Requires Mapy.cz API key in organization settings",
    // Density legend
    densityTitle: "Population Density",
    sparse: "Sparse (<5/km²)",
    low: "Low (5-50/km²)",
    medium: "Medium (50-500/km²)",
    high: "High (>500/km²)",
    // Land use legend
    landuseTitle: "Land Use",
    residential: "Residential",
    commercial: "Commercial",
    industrial: "Industrial",
    agricultural: "Agricultural",
    forest: "Forest",
    water: "Water",
    // Popup labels
    population: "Population",
    density: "Density",
    source: "Source",
    type: "Type"
  },
  
  // Wave 3 Block 3B: Battery Management Extended
  battery: {
    title: "Batteries",
    addBattery: "Add Battery",
    editBattery: "Edit Battery",
    serialNumber: "Serial Number",
    modelName: "Battery Model",
    manufacturer: "Manufacturer",
    capacityWh: "Capacity (Wh)",
    chemistry: "Chemistry",
    chemistryLiPo: "Li-Po (Lithium Polymer)",
    chemistryLiHV: "Li-HV (High Voltage)",
    chemistryLiIon: "Li-Ion",
    chemistryOther: "Other",
    cycleCount: "Cycle Count",
    cycleLimit: "Cycle Limit",
    status: "Status",
    statusActive: "Active",
    statusRetired: "Retired",
    statusMaintenance: "Maintenance",
    manufacturedDate: "Manufactured Date",
    purchasedDate: "Purchased Date",
    retirementDate: "Retirement Date",
    retirementReason: "Retirement Reason",
    compatibleAircraft: "Compatible Aircraft",
    notes: "Notes",
    qrCode: "QR Code",
    printLabels: "Print Labels",
    downloadLabels: "Download Labels (PDF)",
    noBatteriesToPrint: "No batteries to print",
    bulkImport: "Bulk Import",
    importTemplate: "Import Template",
    retire: "Retire",
    retireConfirm: "Are you sure you want to retire this battery?",
    retireReasonRequired: "Please enter a retirement reason",
    incrementCycle: "Add Cycle",
    cycleIncremented: "Cycle incremented",
    batteryRetired: "Battery retired",
    duplicateSerial: "A battery with this serial number already exists",
    cannotUpdateRetired: "Cannot update a retired battery",
    cannotCycleRetired: "Cannot cycle a retired battery",
    searchPlaceholder: "Search by serial number or model...",
    filterByStatus: "Filter by status",
    filterByManufacturer: "Filter by manufacturer",
    lastChargedAt: "Last Charged",
    currentCharge: "Current Charge",
    cycleProgress: "Lifecycle Progress",
    warningThreshold: "Approaching limit",
    criticalThreshold: "Limit exceeded",
    // Bulk import
    importPreview: "Import Preview",
    validRows: "Valid Rows",
    errorRows: "Error Rows",
    downloadTemplate: "Download CSV Template",
    confirmImport: "Confirm Import",
    importSuccess: "Import completed",
    importedCount: "Imported",
    skippedCount: "Skipped",
  },
  
  // Wave 3 Block 3B: Drone Catalog
  catalog: {
    title: "Drone Catalog",
    adminTitle: "Drone Catalog Management",
    addEntry: "Add to Catalog",
    editEntry: "Edit Entry",
    manufacturer: "Manufacturer",
    model: "Model",
    fullName: "Full Name",
    serviceIntervalHours: "Service Interval (hours)",
    serviceIntervalDays: "Service Interval (days)",
    batteryCycleWarning: "Battery Cycle Warning",
    batteryCycleReplacement: "Battery Replacement (cycles)",
    propellerReplacementHours: "Propeller Replacement (hours)",
    defaultRidType: "Default RID Type",
    ridBuiltIn: "Built-in",
    ridModular: "Modular",
    ridNonCompliant: "Non-compliant",
    mtomKg: "MTOM (kg)",
    maxSpeedKmh: "Max Speed (km/h)",
    maxWindMs: "Max Wind (m/s)",
    ingressProtection: "IP Rating",
    deprecated: "Deprecated",
    includeDeprecated: "Show deprecated",
    fromCatalog: "From catalog",
    useFromCatalog: "Use catalog default",
    overridden: "Overridden",
    catalogDefault: "Catalog default",
    selectCatalogEntry: "Select from catalog",
    customNoMatch: "Custom / Not in catalog",
    effectiveIntervals: "Effective Service Intervals",
  },
  
  // Admin Drone Catalog
  admin: {
    droneCatalog: {
      title: "Drone Catalog",
      subtitle: "Manage global drone catalog",
      totalEntries: "Total entries",
      activeEntries: "Active",
      deprecatedEntries: "Deprecated",
      lastUpdated: "Last updated",
    }
  },

  // Correspondence (Sprint Y Block E)
  correspondence: {
    title: "Correspondence",
    subtitle: "Authority communication tracking",
    timeline: "Timeline",
    list: "List",
    filters: "Filters",
    search: "Search correspondence...",
    noCorrespondence: "No correspondence",
    noCorrespondenceDesc: "No authority communication has been recorded yet.",
    
    // Status
    status: {
      pending_review: "Pending Review",
      reviewed: "Reviewed",
      linked: "Linked",
      archived: "Archived",
    },
    
    // Direction
    direction: {
      inbound: "Inbound",
      outbound: "Outbound",
    },
    
    // Source categories
    categories: {
      regulator: "Regulator",
      atc: "ATC",
      operator_authority: "Operator Authority",
      police: "Police",
      fire: "Fire Department",
      energy: "Energy",
      insurance: "Insurance",
      other: "Other",
    },
    
    // Card & Timeline
    from: "From",
    to: "To",
    received: "Received",
    sent: "Sent",
    subject: "Subject",
    attachments: "Attachments",
    attachment: "attachment",
    viewDetails: "View Details",
    viewEmail: "View Email",
    
    // HITL - Human in the Loop (CRITICAL)
    proposed: "Proposed",
    proposedBadge: "proposed — pending verification",
    confirmed: "Confirmed",
    confirmedBadge: "confirmed",
    awaitingReview: "Awaiting operator review",
    needsReview: "Needs Review",
    reviewInQueue: "In review queue",
    goToReviewQueue: "Go to Review Queue",
    
    // Extracted data
    extractedData: "Extracted Data",
    referenceNumber: "Reference Number",
    caseNumber: "Case Number",
    deadline: "Deadline",
    actionRequired: "Action Required",
    priority: "Priority",
    summary: "Summary",
    
    // Links
    linkedTo: "Linked To",
    proposedLink: "Proposed Link",
    confirmedLink: "Confirmed Link",
    authorization: "Authorization",
    mission: "Mission",
    pilot: "Pilot",
    noLinkedEntities: "No linked entities",
    
    // Filters
    filterBySource: "Filter by source",
    filterByStatus: "Filter by status",
    filterByDate: "Filter by date",
    allSources: "All sources",
    allStatuses: "All statuses",
    dateFrom: "From",
    dateTo: "To",
    
    // Stats
    total: "Total",
    pendingReview: "Pending Review",
    overdue: "Overdue",
    thisWeek: "This Week",
    
    // Language A: StatTiles
    statTiles: {
      awaitingReview: "Awaiting Review",
      openCases: "Open Cases",
      deadlinesSoon: "Deadline within 7 days",
    },
    
    // Language A: FilterChips
    filterChips: {
      all: "All",
      needsReview: "Needs Review",
      confirmed: "Confirmed",
      awaitingReply: "Awaiting Reply",
    },
    
    // Detail panel
    selectItem: "Select an item",
    
    // Modal
    emailDetails: "Email Details",
    body: "Body",
    metadata: "Metadata",
    messageId: "Message ID",
    inReplyTo: "In Reply To",
    
    // Tab labels (for integration)
    tabTitle: "Correspondence",
    tabDescription: "Authority communication history",
    
    // Days
    daysAgo: "{days} days ago",
    today: "today",
    yesterday: "yesterday",
    
    // Block G: Review Queue
    review: {
      title: "Correspondence Review Queue",
      subtitle: "HITL commit gate - regulatory data confirmation",
      queueTitle: "Review Queue",
      queueDescription: "Ordered by confidence (lowest first), then deadline urgency",
      emptyQueue: "Queue is empty",
      emptyQueueDesc: "All correspondence has been reviewed.",
      
      // Stats
      statsPending: "Pending Review",
      statsUrgent: "Urgent (with deadline)",
      statsLowConf: "Low Confidence",
      statsReviewedToday: "Reviewed Today",
      
      // HITL
      hitlTitle: "Human-in-the-Loop Requirement",
      hitlDescription: "Every proposed link must be confirmed by an operator before committing to regulatory records. No auto-commit path.",
      
      // Confidence badges
      highConfidence: "High",
      mediumConfidence: "Medium",
      lowConfidence: "Low",
      
      // Deadline badges
      overdue: "Overdue",
      dueToday: "Due Today",
      daysLeft: "days",
      
      // Actions
      confirm: "Confirm",
      reject: "Reject",
      reopen: "Re-open",
      
      // Tabs
      tabEmail: "Email",
      tabExtracted: "Extracted Data",
      tabLinks: "Proposed Links",
      tabAudit: "Audit Log",
      
      // Proposed links
      proposedAuth: "Proposed Authorization",
      proposedMission: "Proposed Mission",
      proposedPilot: "Proposed Pilot",
      
      // Confirm dialog
      confirmTitle: "Confirm Correspondence",
      confirmDescription: "Confirm or edit proposed links. This action commits regulatory data.",
      linksSection: "Entity Links",
      authorizationId: "Authorization ID",
      missionId: "Mission ID",
      pilotId: "Pilot ID",
      caseNumberLabel: "Case Number (to stamp on authorization)",
      caseNumberHint: "If filled and linked to authorization, case number will be stamped on the authorization.",
      editOverrides: "Edit Extracted Data",
      overrideRefNumber: "Override Reference Number",
      overrideDeadline: "Override Deadline",
      overridePriority: "Override Priority",
      overrideSummaryCs: "Override Summary (CS)",
      overrideSummaryEn: "Override Summary (EN)",
      selectPriority: "Select priority",
      notes: "Operator Notes",
      notesPlaceholder: "Optional notes for this review...",
      confirmAction: "Confirm & Commit",
      
      // Case number conflict
      caseNumberConflict: "Case Number Conflict",
      keepExisting: "Keep Existing",
      overwrite: "Overwrite",
      
      // Reject dialog
      rejectTitle: "Reject Correspondence",
      rejectDescription: "Rejected correspondence will be archived. Nothing will be committed to regulatory records.",
      rejectReason: "Rejection Reason",
      rejectReasonPlaceholder: "Enter reason for rejection (required)...",
      rejectAction: "Reject",
      minChars: "Min. 5 characters",
      
      // Reopen dialog
      reopenTitle: "Re-open Correspondence",
      reopenDescription: "Returns item to review queue. Existing confirmed links remain visible.",
      reopenReason: "Re-open Reason",
      reopenReasonPlaceholder: "Enter reason for re-opening...",
      reopenAction: "Re-open",
      
      // Audit
      loadAudit: "Load Audit Log",
      auditTitle: "Change History",
      noAuditEntries: "No audit entries",
      auditOperator: "Operator",
      auditReason: "Reason",
      auditChanges: "Changes",
    },
  },
  
  // Fleet Map Phase 1
  fleetMap: {
    title: "Fleet Map",
    subtitle: "Real-time fleet activity overview",
    
    // Filters
    filterAll: "All",
    filterInFlight: "In Flight",
    filterToday: "Today",
    filterMaintenance: "Maintenance",
    filterAvailable: "Available",
    
    // Time slider
    timeSlider: "Time Slider",
    now: "Now",
    days: "days",
    
    // Status labels
    inFlight: "In Flight",
    scheduledToday: "Scheduled Today",
    scheduledUpcoming: "Scheduled",
    maintenance: "Maintenance",
    available: "Available",
    retired: "Retired",
    
    // Side panel
    aircraftDetails: "Aircraft Details",
    serial: "Serial Number",
    mtom: "MTOM",
    currentMission: "Current Mission",
    upcomingMission: "Upcoming Mission",
    missionName: "Mission Name",
    pilot: "Pilot",
    etot: "Estimated Takeoff",
    eldt: "Estimated Landing",
    viewMission: "View Mission",
    viewAircraft: "View Aircraft",
    inMaintenance: "In Maintenance",
    endsAt: "Ends At",
    type: "Type",
    
    // Equipment
    equipment: "Equipment",
    battery: "Battery",
    rid: "RID Module",
    rtLicense: "RT License",
    active: "Active",
    inactive: "Inactive",
    valid: "Valid",
    expired: "Expired",
    notInstalled: "Not Installed",
    
    // Tier cap
    tierCapTitle: "Free Tier Limit",
    tierCapDescription: "Showing {shown} of {total} aircraft. Upgrade to see full fleet.",
    upgrade: "Upgrade",
  },

  // Block D: Aircraft Detail Expandable Row
  aircraftDetail: {
    // Section titles
    equipment: "Equipment",
    softwareFirmware: "Software & Firmware",
    serviceHistory: "Service History",
    
    // Equipment section
    batteries: "Batteries",
    accessories: "Accessories",
    noEquipment: "No equipment assigned to this aircraft",
    noEquipmentHint: "Assign batteries or accessories to track equipment",
    
    // Status labels
    statusActive: "Active",
    statusMaintenance: "Maintenance",
    statusRetired: "Retired",
    
    // Firmware section
    firmwareVersion: "Firmware Version",
    firmwareUpToDate: "Up to date",
    firmwareUpdateAvailable: "Update available",
    noFirmwareInfo: "No firmware information",
    
    // Service history section
    noServiceRecords: "No service records yet",
    noServiceRecordsHint: "Add maintenance records to track service history",
    addRecord: "Add record",
    workPerformed: "Work performed",
    partsUsed: "Parts used",
    laborHours: "Labor hours",
    technician: "Technician",
    
    // ServiceLogForm
    serviceDate: "Service Date",
    serviceDateHint: "Leave empty for current date",
    technicianPlaceholder: "Technician name",
    workPerformedPlaceholder: "Describe the work performed (min. 5 characters)...",
    partNamePlaceholder: "Part name",
    partNumberPlaceholder: "Part #",
    serviceLogAdded: "Service log added",
    serviceLogFailed: "Failed to add service log",
    workPerformedMinLength: "Work description must be at least 5 characters",
    save: "Save",
    cancel: "Cancel",
    
    // Equipment types
    equipmentTypes: {
      controller: "Controller",
      payload: "Payload",
      gimbal: "Gimbal",
      rtk_module: "RTK Module",
      remote_id_module: "Remote ID Module",
      other: "Other"
    },
    
    // Block E3a: Equipment Suggestion Banner
    equipmentSuggestion: {
      title: "Equipment Suggestion",
      remoteIdSuggestion: "Inventory shows this drone has an external Remote ID module assigned — confirm it is on board?",
      parachuteSuggestion: "Inventory shows this drone has a parachute assigned — confirm it is installed?",
      ftsSuggestion: "Inventory shows this drone has a flight termination system assigned — confirm it is active?",
      geoAwarenessSuggestion: "Inventory shows this drone has a geo-awareness device assigned — confirm it is active?",
      confirmEquipment: "Confirm equipment",
      permissionRequired: "Fleet management role required to confirm equipment",
      confirmed: "Confirmed",
      confirmFailed: "Failed to confirm equipment"
    }
  },
};

export default en;
